import { Injectable, Signal, signal, WritableSignal } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { environment } from 'src/environments/environment'
import {
  AwaitingSubject,
  User,
  Country,
  Presence,
  Factory,
  SubscriberSentinel,
  Subscription,
  Deletable,
  Customer,
  Identifiable,
  Currency,
  Scope,
  Session,
  SessionConfirmed,
  Condition,
} from '../_models'
import { Entity } from '../_models/type/entity-types'
import { firstValueFrom, Observable, Subject } from 'rxjs'
import { Fault } from '../_models/class/fault'
import { LanguageCode } from '../_models/enum/language-code'
import {
  UserPollRequestType,
  UserPollResponseType,
  UserPollErrorResponse,
  UserPollResponse,
  SitePollRequestType,
  SitePollResponseType,
  SitePollErrorResponse,
  SitePollResponse,
  LocationPollRequestType,
  LocationPollResponseType,
  LocationPollErrorResponse,
  LocationPollResponse,
} from './cache-utilities'
import { saveBody } from '../_utils/fcdc-v2'
import { normalizeEntityForStorage, jsonFromStorage } from '../_utils/fcdc-v2'
import { Site } from '../_models/class/site'
import { Location } from '../_models/class/location'
import { PriceCategory } from '../_models/class/price-category'
import {
  Dimensions,
  Locker,
  PaymentTerminalTypeChangeRequest,
} from '../_models/class/locker'
import {
  RefundStrategy,
  SessionTerminateRequest,
} from '../_models/class/session'
import {
  PresencePollErrorResponse,
  PresencePollResponse,
  PresencePollResponseType,
  PresencePollRequestType,
} from './cache-utilities/presence-poll'
import {
  SiteLogPollErrorResponse,
  SiteLogPollResponse,
  SiteLogPollResponseType,
  SiteLogPollRequestType,
} from './cache-utilities/site-log-poll'
import { Column } from '../_models/class/column'
import { Entry } from '../_models/class/log'
import { MessageRequest } from './cache-utilities/message'
import { Router } from '@angular/router'
import { Payment } from '../_models/class/payment'
import { BookingSourceGroup } from '../_models/class/booking-source-group'
import {
  SpotlightColumnSessionsResponse,
  SpotlightEmailRequest,
  SpotlightRequest,
  SpotlightUserResponse,
  SpotlightColumnRequest,
  SpotlightPhoneRequest,
  SpotlightSitesAndLocationsResponse,
  SpotlightSitesAndLocationsRequest,
} from '../_components/spotlight/spotlight-utilities/types'
import {
  PollProgress,
  PollState,
  PollWriteQueue,
  isSettledOrActivePoll,
} from './poll-progress'
import { NetworkDiagnosticsService } from './network-diagnostics.service'
import {
  PollCursor,
  PollCursorState,
  PollKind,
  PollRequestKind,
  autoBackfillTo,
  heavyPollRequest,
  legacyPollCursor,
  mergePollCursors,
  dropFarPollSpans,
  pollSpans,
  pollSpansHold,
  PollSpan,
} from '../_utils/poll-cursor.util'

export { PollState } from './poll-progress'

/** How long pollHeavyCovers waits without the cached edge moving before it gives up. */
const POLL_COVER_STALL_MS = 120_000

@Injectable({
  providedIn: 'root',
})
export class CacheService {
  private readonly apiUrl: string = environment.apiUrl
  private readonly acceptHeader: string = environment.acceptHeader

  public db?: IDBDatabase

  public userPollState = signal<PollState | null>(null)
  public presencePollState = signal<PollState | null>(null)
  public sitePollState = signal<PollState | null>(null)
  public siteLogPollState = signal<PollState | null>(null)
  public locationPollState = signal<PollState | null>(null)

  /** Emits when nav labels (hierarchy, column, locker, signed-in user) should refresh. */
  private readonly hierarchyNavigationRefresh = new Subject<void>()
  readonly hierarchyNavigationRefresh$: Observable<void> =
    this.hierarchyNavigationRefresh.asObservable()

  private notifyNavigationLabelsChanged(): void {
    this.hierarchyNavigationRefresh.next()
  }

  private syncActiveColumnFromUpdate(column: Column): void {
    const cached = this.columnCache?.column
    if (!cached || cached.id !== column.id) {
      return
    }
    this.columnCache!.setColumn(column)
    const lockerId = this.lockerCache?.locker?.id
    if (!lockerId) {
      return
    }
    const locker = column.lockers.find(l => l.id === lockerId)
    if (locker) {
      this.lockerCache!.setLocker(locker)
    }
  }

  private syncActiveColumnAndNotifyNavigation(column: Column): void {
    this.syncActiveColumnFromUpdate(column)
    this.notifyNavigationLabelsChanged()
  }

  private userPoll: Worker
  private sitePoll: Worker
  private siteLogPoll: Worker
  private locationPoll: Worker
  private presencePoll: Worker
  private cache?: UserCache
  private siteCache?: SiteCache
  private siteLogCache?: SiteLogCache
  private locationCache?: LocationCache
  private presenceCache?: PresenceCache
  private userPollWriteQueue = new PollWriteQueue()
  private presencePollWriteQueue = new PollWriteQueue()
  private sitePollWriteQueue = new PollWriteQueue()
  private siteLogPollWriteQueue = new PollWriteQueue()
  private locationPollWriteQueue = new PollWriteQueue()
  private columnCache?: ColumnCache
  private lockerCache?: LockerCache
  private countryCache?: CountryCache
  private customerCache?: CustomerCache

  private users$: AwaitingSubject<User>
  private countries$: AwaitingSubject<Country>
  private customers$: AwaitingSubject<Customer>
  private presences$: AwaitingSubject<Presence>
  private sites$: AwaitingSubject<Site>
  private locations$: AwaitingSubject<Location>
  private lockers$: AwaitingSubject<Locker>
  private sessions$: AwaitingSubject<Session>
  private columns$: AwaitingSubject<Column>
  private lockerDimensions$: AwaitingSubject<Dimensions>
  private lockerPriceCategories$: AwaitingSubject<PriceCategory>
  private logEntries$: AwaitingSubject<Entry>
  private bookingSourceGroups$: AwaitingSubject<BookingSourceGroup>

  private pollRetryCount: number = 0

  private pollCursorStates = new Map<
    string,
    WritableSignal<PollCursorState | null>
  >()
  // When the last poll page of each site, location or presence came (kind:id).
  private pollPageAt = new Map<string, number>()
  private pollHeavyTargets = new Map<string, number>()
  private autoBackfillDone = new Set<string>()
  private pollLockReleases = new Map<PollKind, () => void>()
  private polledByThisTab = new Set<string>()
  private pollChannel: BroadcastChannel

  constructor(
    private http: HttpClient,
    private router: Router,
    private networkDiagnostics: NetworkDiagnosticsService
  ) {
    this.users$ = new AwaitingSubject()
    this.countries$ = new AwaitingSubject()
    this.customers$ = new AwaitingSubject()
    this.presences$ = new AwaitingSubject()
    this.sites$ = new AwaitingSubject()
    this.locations$ = new AwaitingSubject()
    this.lockers$ = new AwaitingSubject()
    this.sessions$ = new AwaitingSubject()
    this.columns$ = new AwaitingSubject()
    this.lockerDimensions$ = new AwaitingSubject()
    this.lockerPriceCategories$ = new AwaitingSubject()
    this.logEntries$ = new AwaitingSubject()
    this.bookingSourceGroups$ = new AwaitingSubject()

    const self = this
    const req = window.indexedDB.open('SpeedBoxDB', environment.dbVersion)

    req.onerror = function (event: Event) {
      const request = event.target as IDBRequest
      console.log('DB onerror: ', request.error?.message)
    }

    req.onsuccess = function (event: Event) {
      const request = event.target as IDBRequest
      const db = request.result as IDBDatabase
      self.db = db
      // Another tab deletes or upgrades the database (a hard reset, a newer
      // version): close it so that can go on, and start again from the root.
      db.onversionchange = () => {
        db.close()
        self.db = undefined
        location.assign('/')
      }
    }

    req.onupgradeneeded = function (event: IDBVersionChangeEvent) {
      const db = req.result
      self.setupDbStores(db, event)
    }

    this.pollChannel = new BroadcastChannel('poll')
    this.pollChannel.onmessage = async function (e) {
      await self.isReady()
      try {
        await self.handlePollChannelMessage(e.data)
      } catch (err) {
        console.error('Error reading poll from another tab:', err)
      }
    }

    this.userPoll = new Worker(
      new URL('./../_workers/user-poll.worker', import.meta.url)
    )
    this.userPoll.onmessage = async function (e) {
      await self.isReady()

      const t = e.data.type as UserPollResponseType
      switch (t) {
        case UserPollResponseType.TokenRefresh:
          if (self.cache) {
            self.cache.accessToken = e.data.data
            localStorage.setItem('accessToken', self.cache.accessToken)
          }
          self.userPollState.set(PollState.Refreshing)
          break
        case UserPollResponseType.Error: {
          const m = e.data.data as UserPollErrorResponse
          console.log('Error received from user poll worker: ' + m.text)
          self.cache?.markFailed(new Error(m.text))
          self.userPollState.set(PollState.Error)
          break
        }
        case UserPollResponseType.Poll: {
          const uc = self.cache
          const pkg = e.data.data as UserPollResponse
          if (uc) {
            self.userPollWriteQueue.enqueue(
              () => self.cache,
              uc,
              () => self.handleUserPollPackage(uc, pkg),
              () => self.userPollState.set(PollState.Error)
            )
          }
          break
        }
        case UserPollResponseType.ConnectionOk:
          self.pollRetryCount = 0
          self.networkDiagnostics.notifyApiSuccess()
          if (self.userPollState() === PollState.Error) {
            console.log('User connection is ok.')
            self.userPollState.set(
              self.cache?.polled ? PollState.Ready : PollState.Loading
            )
          }
          break
        case UserPollResponseType.ConnectionError:
          console.log('User connection is down.')
          try {
            const statusCode = e.data?.code
            if (statusCode === 0) {
              self.networkDiagnostics.report('GET', '/users/{id}/poll')
            }
            if (statusCode === 401 || statusCode === 403) {
              await self.signOut(false)
              break
            }
          } catch {
            console.error('Error checking status code')
          }
          self.userPollState.set(PollState.Error)
          self.pollRetry()
          break
        default:
          throw new Error('Received unrecognized reponse type from user poll.')
      }
    }

    this.presencePoll = new Worker(
      new URL('./../_workers/presence-poll.worker', import.meta.url)
    )
    this.presencePoll.onmessage = async function (e) {
      await self.isReady()

      const t = e.data.type as PresencePollResponseType
      switch (t) {
        case PresencePollResponseType.Error: {
          const m = e.data.data as PresencePollErrorResponse
          console.log('Error received from Presence poll worker: ' + m.text)
          self.presenceCache?.markFailed(new Error(m.text))
          self.presencePollState.set(PollState.Error)
          break
        }
        case PresencePollResponseType.Poll: {
          const pc = self.presenceCache
          const pkg = e.data.data as PresencePollResponse
          if (pc) {
            self.presencePollWriteQueue.enqueue(
              () => self.presenceCache,
              pc,
              () => self.handlePresencePollPackage(pc, pkg),
              () => self.presencePollState.set(PollState.Error)
            )
          }
          break
        }
        case PresencePollResponseType.TokenRefresh:
          if (self.cache) {
            self.cache.accessToken = e.data.data
            localStorage.setItem('accessToken', self.cache.accessToken)
          }
          self.presencePollState.set(PollState.Refreshing)
          break
        case PresencePollResponseType.ConnectionOk:
          self.pollRetryCount = 0
          self.networkDiagnostics.notifyApiSuccess()
          if (self.presencePollState() === PollState.Error) {
            console.log('Presence connection is ok.')
            self.presencePollState.set(
              self.presenceCache?.polled ? PollState.Ready : PollState.Loading
            )
          }
          break
        case PresencePollResponseType.ConnectionError: {
          const statusCode = e.data?.code
          if (statusCode === 0) {
            self.networkDiagnostics.report('GET', '/presences/{id}/poll')
          }
          if (statusCode === 401 || statusCode === 403) {
            console.warn(
              'Presence poll received 401/403, authentication may be invalid.'
            )
            break
          }
          console.log('Presence connection is down.')
          self.presencePollState.set(PollState.Error)
          self.pollRetry()
          break
        }
        default:
          throw new Error(
            'Received unrecognized response type from Presence poll.'
          )
      }
    }

    this.sitePoll = new Worker(
      new URL('./../_workers/site-poll.worker', import.meta.url)
    )
    this.sitePoll.onmessage = async function (e) {
      await self.isReady()
      const t = e.data.type as SitePollResponseType
      switch (t) {
        case SitePollResponseType.Poll: {
          const sc = self.siteCache
          const pkg = e.data.data as SitePollResponse
          if (sc) {
            self.sitePollWriteQueue.enqueue(
              () => self.siteCache,
              sc,
              () => self.handleSitePollPackage(sc, pkg),
              () => self.sitePollState.set(PollState.Error)
            )
          }
          break
        }

        case SitePollResponseType.ConnectionOk:
          self.pollRetryCount = 0
          self.networkDiagnostics.notifyApiSuccess()
          if (self.sitePollState() === PollState.Error) {
            console.log('Site connection is ok.')
            self.sitePollState.set(
              self.siteCache?.polled ? PollState.Ready : PollState.Loading
            )
          }
          break
        case SitePollResponseType.ConnectionError: {
          const statusCode = e.data?.code
          if (statusCode === 0) {
            self.networkDiagnostics.report('GET', '/sites/{id}/poll')
          }
          if (statusCode === 401 || statusCode === 403) {
            console.warn(
              'Site poll received 401/403, authentication may be invalid.'
            )
            break
          }
          console.log('Site connection is down.')
          self.sitePollState.set(PollState.Error)
          self.pollRetry()
          break
        }
        case SitePollResponseType.TokenRefresh:
          console.log('Site poll token refreshed.')
          if (self.cache) {
            self.cache.accessToken = e.data.data
            localStorage.setItem('accessToken', self.cache.accessToken)
          }
          self.sitePollState.set(PollState.Refreshing)
          break
        case SitePollResponseType.Error: {
          const errorData = e.data.data as SitePollErrorResponse
          console.error(
            'Error received from Site poll worker:',
            errorData?.text || 'Unknown error'
          )
          self.siteCache?.markFailed(
            new Error(errorData?.text || 'Unknown error')
          )
          self.sitePollState.set(PollState.Error)
          break
        }
        default:
          console.error(
            'Received unrecognized response type from Site poll:',
            e.data.type
          )
          break
      }
    }

    this.siteLogPoll = new Worker(
      new URL('./../_workers/site-log-poll.worker', import.meta.url)
    )
    this.siteLogPoll.onmessage = async function (e) {
      await self.isReady()
      const t = e.data.type as SiteLogPollResponseType
      switch (t) {
        case SiteLogPollResponseType.Poll: {
          const slc = self.siteLogCache
          const pkg = e.data.data as SiteLogPollResponse
          if (slc) {
            self.siteLogPollWriteQueue.enqueue(
              () => self.siteLogCache,
              slc,
              () => self.handleSiteLogPollPackage(slc, pkg),
              () => self.siteLogPollState.set(PollState.Error)
            )
          }
          break
        }

        case SiteLogPollResponseType.ConnectionOk:
          self.pollRetryCount = 0
          self.networkDiagnostics.notifyApiSuccess()
          if (self.siteLogPollState() === PollState.Error) {
            console.log('Site log connection is ok.')
            self.siteLogPollState.set(
              self.siteLogCache?.polled ? PollState.Ready : PollState.Loading
            )
          }
          break
        case SiteLogPollResponseType.ConnectionError: {
          const statusCode = e.data?.code
          if (statusCode === 0) {
            self.networkDiagnostics.report('GET', '/sites/{id}/log_poll')
          }
          if (statusCode === 401 || statusCode === 403) {
            console.warn(
              'Site log poll received 401/403, authentication may be invalid.'
            )
            break
          }
          console.log('Site log connection is down.')
          self.siteLogPollState.set(PollState.Error)
          self.pollRetry()
          break
        }
        case SiteLogPollResponseType.TokenRefresh:
          console.log('Site log poll token refreshed.')
          if (self.cache) {
            self.cache.accessToken = e.data.data
            localStorage.setItem('accessToken', self.cache.accessToken)
          }
          self.siteLogPollState.set(PollState.Refreshing)
          break
        case SiteLogPollResponseType.Error: {
          const errorData = e.data.data as SiteLogPollErrorResponse
          console.error(
            'Error received from Site log poll worker:',
            errorData?.text || 'Unknown error'
          )
          self.siteLogCache?.markFailed(
            new Error(errorData?.text || 'Unknown error')
          )
          self.siteLogPollState.set(PollState.Error)
          break
        }
        default:
          console.error(
            'Received unrecognized response type from Site log poll:',
            e.data.type
          )
          break
      }
    }

    this.locationPoll = new Worker(
      new URL('./../_workers/location-poll.worker', import.meta.url)
    )
    this.locationPoll.onmessage = async function (e) {
      await self.isReady()

      const t = e.data.type as LocationPollResponseType
      switch (t) {
        case LocationPollResponseType.Error: {
          const m = e.data.data as LocationPollErrorResponse
          console.log('Error received from Location poll worker: ' + m.text)
          self.locationCache?.markFailed(new Error(m.text))
          self.locationPollState.set(PollState.Error)
          break
        }
        case LocationPollResponseType.Poll: {
          const lc = self.locationCache
          const pkg = e.data.data as LocationPollResponse
          if (lc) {
            self.locationPollWriteQueue.enqueue(
              () => self.locationCache,
              lc,
              () => self.handleLocationPollPackage(lc, pkg),
              () => self.locationPollState.set(PollState.Error)
            )
          }
          break
        }

        case LocationPollResponseType.TokenRefresh:
          if (self.cache) {
            self.cache.accessToken = e.data.data
            localStorage.setItem('accessToken', self.cache.accessToken)
          }
          self.locationPollState.set(PollState.Refreshing)
          break
        case LocationPollResponseType.ConnectionOk:
          self.pollRetryCount = 0
          self.networkDiagnostics.notifyApiSuccess()
          if (self.locationPollState() === PollState.Error) {
            console.log('Location connection is ok.')
            self.locationPollState.set(
              self.locationCache?.polled ? PollState.Ready : PollState.Loading
            )
          }
          break
        case LocationPollResponseType.ConnectionError: {
          const statusCode = e.data?.code
          if (statusCode === 0) {
            self.networkDiagnostics.report(
              'GET',
              '/sites/{id}/locations/{id}/poll'
            )
          }
          if (statusCode === 401 || statusCode === 403) {
            console.warn(
              'Location poll received 401/403, authentication may be invalid.'
            )
            break
          }
          console.log('Location connection is down.')
          self.locationPollState.set(PollState.Error)
          self.pollRetry()
          break
        }
        default:
          throw new Error(
            'Received unrecognized response type from Location poll.'
          )
      }
    }
  }

  //#region polling
  async setupDbStores(
    db: IDBDatabase,
    version: IDBVersionChangeEvent | number
  ) {
    const v =
      version instanceof IDBVersionChangeEvent ? version.oldVersion : version
    const request =
      version instanceof IDBVersionChangeEvent
        ? (version.target as IDBOpenDBRequest)
        : null

    if (v < 1) {
      db.createObjectStore('users', { keyPath: 'id' })
      db.createObjectStore('countries', { keyPath: 'id' })
      db.createObjectStore('customers', { keyPath: 'id' })
      db.createObjectStore('presences', { keyPath: 'id' })
      db.createObjectStore('sites', { keyPath: 'id' })
      db.createObjectStore('locations', { keyPath: 'id' })
      db.createObjectStore('locker_dimensions', { keyPath: 'id' })
      db.createObjectStore('locker_price_categories', { keyPath: 'id' })
      db.createObjectStore('columns', { keyPath: 'id' })
      const sessionsStore = db.createObjectStore('sessions', { keyPath: 'id' })
      try {
        sessionsStore.createIndex('created', 'created', { unique: false })
      } catch (error) {
        console.error(
          'Failed to create "created" index for sessions store:',
          error
        )
      }
      db.createObjectStore('log_entries', { keyPath: 'id' })
      db.createObjectStore('payments', { keyPath: 'id' })
    }
    if (v < 2) {
      db.createObjectStore('conversion_rates', { keyPath: 'id' })
    }
    if (v < 5) {
      db.createObjectStore('booking_source_groups', { keyPath: 'id' })
    }
    if (v < 6) {
      const tx = request?.transaction
      if (tx) {
        const logEntriesStore = tx.objectStore('log_entries')
        if (logEntriesStore) {
          logEntriesStore.createIndex('pk', 'pk', { unique: false })
          logEntriesStore.createIndex('sourceId', 'source.id', {
            unique: false,
          })
          logEntriesStore.createIndex('userId', 'userId', { unique: false })
          logEntriesStore.createIndex('sessionId', 'sessionId', {
            unique: false,
          })
          logEntriesStore.createIndex('columnId', 'columnId', { unique: false })
          try {
            logEntriesStore.createIndex('created', 'created', { unique: false })
          } catch (error) {
            console.error(
              'Failed to create "created" index for log_entries store:',
              error
            )
          }
          logEntriesStore.createIndex('modified', 'modified', { unique: false })
        } else {
          console.error('Log entries store not found in database')
        }
        const sessionsStore = tx.objectStore('sessions')
        if (sessionsStore) {
          sessionsStore.createIndex('siteId', 'siteId', { unique: false })
          sessionsStore.createIndex('lockerId', 'lockerId', { unique: false })
          sessionsStore.createIndex('countryCode', 'countryCode', {
            unique: false,
          })
          sessionsStore.createIndex('customerId', 'customerId', {
            unique: false,
          })
          sessionsStore.createIndex('presenceId', 'presenceId', {
            unique: false,
          })
          sessionsStore.createIndex('locationId', 'locationId', {
            unique: false,
          })
          sessionsStore.createIndex('columnId', 'columnId', { unique: false })
          sessionsStore.createIndex('userId', 'userId', { unique: false })
          sessionsStore.createIndex('modified', 'modified', { unique: false })
        } else {
          console.error('Sessions store not found in database')
        }

        const presencesStore = tx.objectStore('presences')

        if (presencesStore) {
          try {
            presencesStore.createIndex('customerId', 'customerId', {
              unique: false,
            })
            presencesStore.createIndex('countryCode', 'countryCode', {
              unique: false,
            })
            try {
              presencesStore.createIndex('created', 'created', {
                unique: false,
              })
            } catch (error) {
              console.error(
                'Failed to create "created" index for presences store:',
                error
              )
            }
            presencesStore.createIndex('modified', 'modified', {
              unique: false,
            })
          } catch (error) {
            console.warn('Error creating presences indexes:', error)
          }
        }

        const sitesStore = tx.objectStore('sites')

        if (sitesStore) {
          try {
            sitesStore.createIndex('customerId', 'customerId', {
              unique: false,
            })
            sitesStore.createIndex('countryCode', 'countryCode', {
              unique: false,
            })
            sitesStore.createIndex('presenceId', 'presenceId', {
              unique: false,
            })
            try {
              sitesStore.createIndex('created', 'created', {
                unique: false,
              })
            } catch (error) {
              console.error(
                'Failed to create "created" index for sites store:',
                error
              )
            }
            sitesStore.createIndex('modified', 'modified', {
              unique: false,
            })
          } catch (error) {
            console.warn('Error creating sites indexes:', error)
          }
        }

        const locationsStore = tx.objectStore('locations')

        if (locationsStore) {
          try {
            locationsStore.createIndex('siteId', 'siteId', {
              unique: false,
            })
            try {
              locationsStore.createIndex('created', 'created', {
                unique: false,
              })
            } catch (error) {
              console.error(
                'Failed to create "created" index for locations store:',
                error
              )
            }
            locationsStore.createIndex('modified', 'modified', {
              unique: false,
            })
          } catch (error) {
            console.warn('Error creating locations indexes:', error)
          }
        }

        const columnsStore = tx.objectStore('columns')

        if (columnsStore) {
          try {
            columnsStore.createIndex('countryCode', 'countryCode', {
              unique: false,
            })
            columnsStore.createIndex('presenceId', 'presenceId', {
              unique: false,
            })
            columnsStore.createIndex('siteId', 'siteId', {
              unique: false,
            })
            columnsStore.createIndex('locationId', 'locationId', {
              unique: false,
            })
            columnsStore.createIndex('hardwareId', 'hardwareId', {
              unique: false,
            })
            try {
              columnsStore.createIndex('created', 'created', {
                unique: false,
              })
            } catch (error) {
              console.error(
                'Failed to create "created" index for columns store:',
                error
              )
            }
            columnsStore.createIndex('modified', 'modified', {
              unique: false,
            })
          } catch (error) {
            console.warn('Error creating columns indexes:', error)
          }
        }

        const bookingSourceGroupsStore = tx.objectStore('booking_source_groups')

        if (bookingSourceGroupsStore) {
          try {
            try {
              bookingSourceGroupsStore.createIndex('created', 'created', {
                unique: false,
              })
            } catch (error) {
              console.error(
                'Failed to create "created" index for booking_source_groups store:',
                error
              )
            }
            bookingSourceGroupsStore.createIndex('modified', 'modified', {
              unique: false,
            })
          } catch (error) {
            console.warn('Error creating booking source groups indexes:', error)
          }
        }

        const conversionRatesStore = tx.objectStore('conversion_rates')

        if (conversionRatesStore) {
          try {
            conversionRatesStore.createIndex('timeStamp', 'timeStamp', {
              unique: false,
            })
            conversionRatesStore.createIndex('currency', 'currency', {
              unique: false,
            })
            try {
              conversionRatesStore.createIndex('created', 'created', {
                unique: false,
              })
            } catch (error) {
              console.error(
                'Failed to create "created" index for conversion_rates store:',
                error
              )
            }
          } catch (error) {
            console.warn('Error creating conversion rates indexes:', error)
          }
        }

        const usersStore = tx.objectStore('users')

        if (usersStore) {
          try {
            usersStore.createIndex('countryCode', 'countryCode', {
              unique: false,
            })
            try {
              usersStore.createIndex('created', 'created', {
                unique: false,
              })
            } catch (error) {
              console.error(
                'Failed to create "created" index for users store:',
                error
              )
            }
            usersStore.createIndex('modified', 'modified', {
              unique: false,
            })
          } catch (error) {
            console.warn('Error creating users indexes:', error)
          }
        }

        const paymentsStore = tx.objectStore('payments')
        if (paymentsStore) {
          try {
            paymentsStore.createIndex('userId', 'userId', { unique: false })
            paymentsStore.createIndex('currency', 'currency', {
              unique: false,
            })
            try {
              paymentsStore.createIndex('created', 'created', { unique: false })
            } catch (error) {
              console.error(
                'Failed to create "created" index for payments store:',
                error
              )
            }
            paymentsStore.createIndex('modified', 'modified', {
              unique: false,
            })
          } catch (error) {
            console.warn('Error creating payments indexes:', error)
          }
        }
      }
    }
    if (v < 7) {
      const visitorsAndAvailabilityStore = db.createObjectStore(
        'visitors_and_availability',
        { keyPath: 'id' }
      )
      try {
        visitorsAndAvailabilityStore.createIndex('created', 'created', {
          unique: false,
        })
      } catch (error) {
        console.error(
          'Failed to create "created" index for visitors_and_availability store:',
          error
        )
      }

      const indexedDashboardDataStore = db.createObjectStore(
        'indexed_dashboard_data',
        { keyPath: 'id' }
      )
      try {
        indexedDashboardDataStore.createIndex('date', 'date', { unique: false })
      } catch (error) {
        console.error(
          'Failed to create "date" index for indexed_dashboard_data store:',
          error
        )
      }
      try {
        indexedDashboardDataStore.createIndex('entityId', 'entityId', {
          unique: false,
        })
      } catch (error) {
        console.error(
          'Failed to create "entityId" index for indexed_dashboard_data store:',
          error
        )
      }
    }
    if (v < 8) {
      const reportsDailyStore = db.createObjectStore('reportsDaily', {
        keyPath: 'id',
      })

      try {
        reportsDailyStore.createIndex('byEntityDate', ['entityId', 'date'], {
          unique: false,
        })
      } catch (error) {
        console.error(
          'Failed to create "byEntityDate" index for reportsDaily store:',
          error
        )
      }

      try {
        reportsDailyStore.createIndex(
          'byCategoryEntityDate',
          ['category', 'entityId', 'date'],
          {
            unique: false,
          }
        )
      } catch (error) {
        console.error(
          'Failed to create "byCategoryEntityDate" index for reportsDaily store:',
          error
        )
      }
    }
    if (v < 9) {
      db.createObjectStore('poll_cursors', { keyPath: 'id' })
    }
  }

  pollRetry() {
    if (this.pollRetryCount >= 90) {
      console.error('Unable to poll, signing out.')
      this.pollRetryCount = 0
      if (this.cache?.user?.email) {
        localStorage.setItem('persistedUser', this.cache.user.email.address)
      }
      this.signOut(true)
      this.router.navigate(['/'])
    } else {
      this.pollRetryCount++
      console.error('Retrying, count: ' + this.pollRetryCount)
    }
  }

  async parsePollData<T extends Deletable>(
    cache: PollProgress,
    data: T[],
    classIdentifier: any,
    observable: AwaitingSubject<T>
  ): Promise<void> {
    if (!data) return

    const factory = classIdentifier.getFactory()
    const promises = data.map(async item => {
      try {
        const o = factory.make(item)
        await this.putEntity(o, factory)
        if (cache.polled) {
          await observable.next(o)
        }
      } catch (x) {
        console.error(
          'Error parsing ' + classIdentifier.name + ' from poll.',
          x,
          item
        )
      }
    })

    await Promise.all(promises)

    if (data.length > 0 && cache.polled) {
      await observable.next(new SubscriberSentinel())
    }
  }

  async batchParsePollData<T>(
    cache: PollProgress,
    data: T[],
    classIdentifier: any,
    observable: AwaitingSubject<T>
  ) {
    const db = this.db
    if (!db) {
      throw new Error('DB not initialized.')
    }

    if (!data) {
      return
    }

    const BATCH_SIZE: number = 10000
    const batches: number = Math.ceil(data.length / BATCH_SIZE)

    const factory = classIdentifier.getFactory()
    const table = factory.getTableName()

    let currentIndex = 0

    for (let currentBatch = 1; currentBatch <= batches; currentBatch++) {
      await new Promise((resolve, reject) => {
        const transaction = db.transaction([table], 'readwrite')
        const store = transaction.objectStore(table)

        let batchEnd = currentBatch * BATCH_SIZE - 1
        batchEnd = batchEnd > data.length ? data.length : batchEnd

        while (currentIndex < batchEnd) {
          const o = factory.make(data[currentIndex])
          store.put(normalizeEntityForStorage(o, factory))
          if (observable.subCount > 0) {
            observable.next(o)
          }
          currentIndex++
        }

        transaction.oncomplete = event => {
          resolve(event)
        }

        transaction.onerror = event => {
          reject(event)
        }

        transaction.commit()
      })
    }

    if (data.length > 0 && cache.polled) {
      await observable.next(new SubscriberSentinel())
    }
  }

  async userHasPolled(): Promise<void> {
    if (!this.cache) {
      throw new Error('Checking if User has polled without being signed in.')
    }
    await this.isReady()
    await this.cache.hasPolled()
  }

  async siteHasPolled(): Promise<void> {
    if (!this.siteCache) {
      throw new Error("Can't await Site poll without having chosen a Site.")
    }
    await this.isReady()
    await this.siteCache.hasPolled()
  }

  async locationHasPolled(): Promise<void> {
    if (!this.locationCache) {
      throw new Error(
        "Can't await Location poll without having chosen a Location."
      )
    }
    await this.isReady()
    await this.locationCache.hasPolled()
  }

  async presenceHasPolled(): Promise<void> {
    if (!this.presenceCache) {
      throw new Error(
        "Can't await presence poll without having chosen a presence."
      )
    }
    await this.isReady()
    await this.presenceCache.hasPolled()
  }

  async userHasOverviewData(): Promise<void> {
    if (!this.cache) {
      throw new Error("Can't await user overview data without being signed in.")
    }
    await this.isReady()
    await this.cache.hasOverviewData()
  }

  async siteHasOverviewData(): Promise<void> {
    if (!this.siteCache) {
      throw new Error(
        "Can't await site overview data without having chosen a site."
      )
    }
    await this.isReady()
    await this.siteCache.hasOverviewData()
  }

  async locationHasOverviewData(): Promise<void> {
    if (!this.locationCache) {
      throw new Error(
        "Can't await location overview data without having chosen a location."
      )
    }
    await this.isReady()
    await this.locationCache.hasOverviewData()
  }

  async presenceHasOverviewData(): Promise<void> {
    if (!this.presenceCache) {
      throw new Error(
        "Can't await presence overview data without having chosen a presence."
      )
    }
    await this.isReady()
    await this.presenceCache.hasOverviewData()
  }

  private commitPollPackage(
    cache: PollProgress,
    pkg: { id: number; done?: boolean },
    etag: string | undefined,
    cursorKey: string | undefined,
    state: WritableSignal<PollState | null>
  ): void {
    if (pkg.done) {
      cache.setFinalInitialPollPackage(pkg.id)
    }
    cache.setReceivedInitialPollPackage(pkg.id)

    if (pkg.done && etag && cursorKey) {
      localStorage.setItem(cursorKey, etag)
    }

    state.set(cache.polled ? PollState.Ready : PollState.Loading)
  }

  /**
   * writes one poll page and its cursor in a single readwrite transaction.
   * The stored cursor only grows: it is merged inside the transaction.
   * Heavy entries whose server-set modified equals the stored one are not
   * written again and not emitted. Other tabs are told what was written.
   */
  private async commitPollPage(
    cache: PollProgress,
    lists: {
      data: any[]
      classIdentifier: any
      observable: AwaitingSubject<any>
      heavy?: boolean
    }[],
    kind: PollKind,
    id: string,
    cursor: PollCursor | undefined,
    backfilling: boolean
  ): Promise<void> {
    const cursorId = kind + ':' + id
    this.pollPageAt.set(cursorId, Date.now())
    const db = this.db
    if (!db) {
      throw new Error('DB not initialized.')
    }

    const pages = lists
      .filter(l => l.data && l.data.length > 0)
      .map(l => {
        const factory = l.classIdentifier.getFactory()
        return {
          factory: factory,
          table: factory.getTableName() as string,
          data: l.data,
          objects: l.data.map(d => factory.make(d)),
          written: [] as any[],
          observable: l.observable,
          heavy: l.heavy,
        }
      })

    // Log entries have no modified; they never change after created.
    const modified = (json: any) =>
      new Date(json?.modified ?? json?.created).getTime()

    const heavyTable = kind === PollKind.SiteLog ? 'log_entries' : 'sessions'
    const tables = new Set(pages.map(p => p.table))
    tables.add('poll_cursors')
    if (cursor) {
      tables.add(heavyTable)
    }

    let merged: PollCursor | undefined

    await new Promise((resolve, reject) => {
      const transaction = db.transaction(Array.from(tables), 'readwrite')

      for (const page of pages) {
        const store = transaction.objectStore(page.table)
        page.objects.forEach((o, i) => {
          if (!page.heavy) {
            store.put(normalizeEntityForStorage(o, page.factory))
            page.written.push(o)
            return
          }

          const req = store.get(o.id)
          req.onsuccess = () => {
            const m = modified(page.data[i])
            if (
              !req.result ||
              isNaN(m) ||
              modified(jsonFromStorage(req.result)) !== m
            ) {
              store.put(normalizeEntityForStorage(o, page.factory))
              page.written.push(o)
            }
          }
        })
      }

      if (cursor) {
        const store = transaction.objectStore('poll_cursors')
        const req = store.get(cursorId)
        req.onsuccess = () => {
          const far = dropFarPollSpans(mergePollCursors(req.result, cursor))
          merged = far.cursor
          store.put({ id: cursorId, ...merged })
          if (far.dropped) {
            const keep = new Set<string>(
              pages
                .filter(p => p.table === heavyTable)
                .flatMap(p => p.objects.map(o => o.id))
            )
            this.dropFarRows(transaction, kind, id, far.dropped, keep)
          }
        }
      }

      transaction.oncomplete = event => {
        resolve(event)
      }

      transaction.onerror = event => {
        reject(event)
      }
    })

    for (const page of pages) {
      if (page.observable.subCount > 0) {
        for (const o of page.written) {
          await page.observable.next(o)
        }
      }
      if (page.written.length > 0 && cache.polled) {
        await page.observable.next(new SubscriberSentinel())
      }
    }

    this.setPollCursorState(cursorId, merged, backfilling)

    const written: Record<string, string[]> = {}
    for (const page of pages) {
      if (page.written.length > 0) {
        written[page.table] = (written[page.table] ?? []).concat(
          page.written.map(o => o.id)
        )
      }
    }
    this.pollChannel.postMessage({
      type: 'commit',
      kind: kind,
      id: id,
      cursor: merged,
      backfilling: backfilling,
      written: written,
    })
  }

  /**
   * deletes the cached rows of a site, location, presence or site log modified
   * (log entries: created) inside one of the dropped spans, too far off to
   * fill. Rows in keep, just received, stay, and so do sessions another
   * cursor's span still holds.
   */
  private dropFarRows(
    transaction: IDBTransaction,
    kind: PollKind,
    id: string,
    dropped: PollSpan[],
    keep: Set<string>
  ): void {
    const cursorId = kind + ':' + id
    const inDropped = (m: number) =>
      dropped.some(span => span.oldest <= m && m <= span.newest)

    if (kind === PollKind.SiteLog) {
      const request = transaction
        .objectStore('log_entries')
        .index('created')
        .openCursor(
          IDBKeyRange.bound(
            new Date(dropped[dropped.length - 1].oldest * 1000).toISOString(),
            new Date(dropped[0].newest * 1000).toISOString()
          )
        )
      request.onsuccess = () => {
        const row = request.result
        if (!row) {
          return
        }
        const json = jsonFromStorage(row.value) as any
        const m = Math.floor(new Date(json?.created).getTime() / 1000)
        if (
          (json?.siteId === id || json?.pk === id) &&
          inDropped(m) &&
          !keep.has(json.id)
        ) {
          row.delete()
        }
        row.continue()
      }
      return
    }

    const cursors = transaction.objectStore('poll_cursors').getAll()
    cursors.onsuccess = () => {
      const spans = new Map<string, PollSpan[]>(
        (cursors.result as ({ id: string } & PollCursor)[])
          .filter(c => c.id !== cursorId)
          .map(c => [c.id, pollSpans(c)])
      )
      const held = (json: any, m: number) =>
        [
          PollKind.Site + ':' + json.siteId,
          PollKind.Location + ':' + json.locationId,
          PollKind.Presence + ':' + json.presenceId,
        ].some(c =>
          spans.get(c)?.some(span => span.oldest <= m && m <= span.newest)
        )

      const index =
        kind === PollKind.Location
          ? 'locationId'
          : kind === PollKind.Presence
            ? 'presenceId'
            : 'siteId'
      const request = transaction
        .objectStore('sessions')
        .index(index)
        .openCursor(IDBKeyRange.only(id))
      request.onsuccess = () => {
        const row = request.result
        if (!row) {
          return
        }
        const json = jsonFromStorage(row.value) as any
        const m = Math.floor(new Date(json?.modified).getTime() / 1000)
        if (inDropped(m) && !keep.has(json.id) && !held(json, m)) {
          row.delete()
        }
        row.continue()
      }
    }
  }

  /**
   * reads a poll cursor from IndexedDB. A string cursor left in localStorage
   * by an earlier version is moved there, read as complete up to it.
   */
  /**
   * the user poll's ETag, kept in IndexedDB with the data it describes: a cache
   * emptied by a reset (in this tab or another) loses it too, so the next user
   * poll asks for everything instead of only the changes.
   */
  private async getUserCursor(userId: string): Promise<string> {
    await this.isReady()
    const db = this.db
    if (!db || !db.objectStoreNames.contains('poll_cursors')) {
      return ''
    }
    return new Promise<string>(resolve => {
      const req = db
        .transaction('poll_cursors', 'readonly')
        .objectStore('poll_cursors')
        .get('user:' + userId)
      req.onsuccess = () => resolve(req.result?.etag ?? '')
      req.onerror = () => resolve('')
    })
  }

  private async putUserCursor(userId: string, etag: string): Promise<void> {
    await this.isReady()
    const db = this.db
    if (!db) {
      return
    }
    await new Promise<void>(resolve => {
      const t = db.transaction('poll_cursors', 'readwrite')
      t.objectStore('poll_cursors').put({ id: 'user:' + userId, etag: etag })
      t.oncomplete = () => resolve()
      t.onerror = () => resolve()
    })
    localStorage.removeItem('userCursor' + userId)
  }

  private async getPollCursor(
    kind: PollKind,
    id: string
  ): Promise<PollCursor | undefined> {
    const cursorId = kind + ':' + id
    const legacyCursorKey =
      (kind === PollKind.SiteLog ? 'siteLog' : kind) + 'Cursor' + id
    await this.isReady()
    const db = this.db
    if (!db) {
      throw new Error('DB not initialized.')
    }

    const legacy = localStorage.getItem(legacyCursorKey)

    const cursor = await new Promise<PollCursor | undefined>(
      (resolve, reject) => {
        const transaction = db.transaction(['poll_cursors'], 'readwrite')
        const store = transaction.objectStore('poll_cursors')
        let cursor: PollCursor | undefined

        const req = store.get(cursorId)
        req.onsuccess = () => {
          cursor = req.result
          if (!cursor && legacy) {
            cursor = legacyPollCursor(legacy)
            if (cursor) {
              store.put({ id: cursorId, ...cursor })
            }
          }
        }

        transaction.oncomplete = () => {
          resolve(cursor)
        }

        transaction.onerror = event => {
          reject(event)
        }
      }
    )

    if (legacy) {
      localStorage.removeItem(legacyCursorKey)
    }

    return cursor
  }

  private getPollCursorSignal(
    cursorId: string
  ): WritableSignal<PollCursorState | null> {
    let state = this.pollCursorStates.get(cursorId)
    if (!state) {
      state = signal<PollCursorState | null>(null)
      this.pollCursorStates.set(cursorId, state)
    }
    return state
  }

  private setPollCursorState(
    cursorId: string,
    cursor: PollCursor | undefined,
    backfilling: boolean
  ): void {
    if (!cursor) {
      return
    }

    const target = this.pollHeavyTargets.get(cursorId)
    if (target !== undefined && cursor.heavyOldest <= target) {
      this.pollHeavyTargets.delete(cursorId)
    }

    this.getPollCursorSignal(cursorId).set({
      lightSyncedTo: cursor.lightSyncedTo,
      heavyNewest: cursor.heavyNewest,
      heavyOldest: cursor.heavyOldest,
      backfillWindow: cursor.backfillWindow,
      olderSpans: cursor.olderSpans,
      backfilling: backfilling,
    })
  }

  /**
   * cursor of a polled site, location or presence. Null until it
   * has a cursor in IndexedDB.
   */
  pollCursorState(kind: PollKind, id: string): Signal<PollCursorState | null> {
    const cursorId = kind + ':' + id
    const exists = this.pollCursorStates.has(cursorId)
    const state = this.getPollCursorSignal(cursorId)

    if (!exists) {
      this.getPollCursor(kind, id)
        .then(cursor => {
          if (state() === null) {
            this.setPollCursorState(cursorId, cursor, false)
          }
        })
        .catch(err => console.error('Failed to read poll cursor:', err))
    }

    return state.asReadonly()
  }

  /**
   * polls the heavy lists of a site, location or presence back to date
   * in backfill windows, until its heavyOldest is at or before date.
   */
  async pollHeavyBackTo(kind: PollKind, id: string, date: Date): Promise<void> {
    const cursorId = kind + ':' + id
    let to = Math.floor(date.getTime() / 1000)

    const target = this.pollHeavyTargets.get(cursorId)
    if (target !== undefined && target < to) {
      to = target
    }

    if (!this.polledByThisTab.has(cursorId)) {
      this.pollHeavyTargets.set(cursorId, to)
      this.pollChannel.postMessage({
        type: 'backfill',
        kind: kind,
        id: id,
        to: to,
      })
      return
    }

    switch (kind) {
      case PollKind.Site:
        if (this.siteCache?.siteId === id) {
          to = this.backfillDepth(to, this.siteCache.site?.created)
          this.sitePoll.postMessage({
            type: SitePollRequestType.Backfill,
            siteId: id,
            backfillTo: to,
          })
        }
        break
      case PollKind.SiteLog:
        if (this.siteLogCache?.siteId === id) {
          to = this.backfillDepth(to, this.siteCache?.site?.created)
          this.siteLogPoll.postMessage({
            type: SiteLogPollRequestType.Backfill,
            siteId: id,
            backfillTo: to,
          })
        }
        break
      case PollKind.Location:
        if (this.locationCache?.locationId === id) {
          to = this.backfillDepth(to, this.locationCache.location?.created)
          this.locationPoll.postMessage({
            type: LocationPollRequestType.Backfill,
            locationId: id,
            backfillTo: to,
          })
        }
        break
      case PollKind.Presence:
        if (this.presenceCache?.presenceId === id) {
          to = this.backfillDepth(to, this.presenceCache.presence?.created)
          this.presencePoll.postMessage({
            type: PresencePollRequestType.Backfill,
            presenceId: id,
            backfillTo: to,
          })
        }
        break
    }

    this.pollHeavyTargets.set(cursorId, to)
  }

  // Backfill stops at the creation date of the polled site, location or
  // presence.
  private backfillDepth(to: number, created?: Date): number {
    const c = created ? Math.floor(created.getTime() / 1000) : NaN
    return isNaN(c) ? to : Math.max(to, c)
  }

  /**
   * after a finished poll request, sends the automatic backfill its next step
   * (autoBackfillTo); pages that need older data call pollHeavyBackTo themselves.
   */
  private async autoBackfill(
    kind: PollKind,
    id: string,
    cursor: PollCursor,
    created?: Date
  ): Promise<void> {
    const cursorId = kind + ':' + id
    if (this.autoBackfillDone.has(cursorId)) {
      return
    }

    const c = created ? Math.floor(created.getTime() / 1000) : 0
    const cached = await this.countCachedHeavy(kind, id, cursor.heavyOldest)
    // The site log fills its first window only; older entries load on request.
    const to = autoBackfillTo(
      cursor,
      cached,
      isNaN(c) ? 0 : c,
      undefined,
      kind === PollKind.SiteLog ? 24 * 60 * 60 : undefined
    )
    if (to === undefined) {
      // A cursor about to start a new span is not done: the gap behind the
      // new span is filled next.
      if (heavyPollRequest(cursor).kind !== PollRequestKind.NewSpan) {
        this.autoBackfillDone.add(cursorId)
      }
      return
    }

    await this.pollHeavyBackTo(kind, id, new Date(to * 1000))
  }

  /**
   * counts the cached heavy rows of a site, location or site log: sessions by
   * their siteId or locationId index, log entries created since heavyOldest
   * (the log store is shared between sites, so they are matched by site).
   */
  private async countCachedHeavy(
    kind: PollKind,
    id: string,
    heavyOldest: number
  ): Promise<number> {
    await this.isReady()
    const table = kind === PollKind.SiteLog ? 'log_entries' : 'sessions'

    return new Promise<number>((resolve, reject) => {
      const transaction = this.db!.transaction([table], 'readonly')
      const objectStore = transaction.objectStore(table)

      if (kind !== PollKind.SiteLog) {
        const index = kind === PollKind.Location ? 'locationId' : 'siteId'
        const request = objectStore.index(index).count(IDBKeyRange.only(id))
        request.onsuccess = () => resolve(request.result)
        request.onerror = () => reject(request.error)
        return
      }

      let n = 0
      const request = objectStore
        .index('created')
        .openCursor(
          IDBKeyRange.lowerBound(new Date(heavyOldest * 1000).toISOString())
        )
      request.onsuccess = () => {
        const cursor = request.result
        if (!cursor) {
          resolve(n)
          return
        }
        const json = jsonFromStorage(cursor.value) as any
        if (json?.siteId === id || json?.pk === id) {
          n++
        }
        cursor.continue()
      }
      request.onerror = () => reject(request.error)
    })
  }

  /**
   * persists one user poll package. User, countries, customers, and presences
   * are written before lockers and extra users so country overview can render
   * as soon as its required data is durable.
   */
  private async handleUserPollPackage(
    cache: UserCache,
    pkg: UserPollResponse
  ): Promise<void> {
    if (this.cache !== cache || cache.hasFailed()) {
      return
    }

    try {
      const data = pkg.data

      if (data?.user) {
        await this.parsePollData(cache, [data.user], User, this.users$)
      }

      if (data?.countries) {
        await this.batchParsePollData(
          cache,
          data.countries,
          Country,
          this.countries$
        )
        const activeCountryId = this.countryCache?.country?.id
        if (activeCountryId) {
          for (const countryJson of data.countries) {
            const polledCountry = Country.getFactory().make(countryJson)
            if (polledCountry.id === activeCountryId) {
              this.countryCache?.setCountry(polledCountry)
              break
            }
          }
        }
      }

      if (data?.customers) {
        await this.batchParsePollData(
          cache,
          data.customers,
          Customer,
          this.customers$
        )
      }

      if (data?.presences) {
        await this.batchParsePollData(
          cache,
          data.presences,
          Presence,
          this.presences$
        )
        const presenceCache = this.presenceCache
        if (presenceCache) {
          for (const presenceJson of data.presences) {
            const polledPresence = Presence.getFactory().make(presenceJson)
            if (presenceCache.presenceId === polledPresence.id) {
              presenceCache.setPresence(polledPresence)
              break
            }
          }
        }
      }

      if (pkg.done) {
        cache.markOverviewReady()
      }

      if (data?.lockerDimensions) {
        await this.batchParsePollData(
          cache,
          data.lockerDimensions,
          Dimensions,
          this.lockerDimensions$
        )
      }

      if (data?.lockerPriceCategories) {
        await this.batchParsePollData(
          cache,
          data.lockerPriceCategories,
          PriceCategory,
          this.lockerPriceCategories$
        )
      }

      if (data?.lockers) {
        await this.batchParsePollData(
          cache,
          data.lockers,
          Locker,
          this.lockers$
        )
      }

      if (data?.users) {
        await this.batchParsePollData(cache, data.users, User, this.users$)
      }

      if (data?.bookingSourceGroups) {
        await this.batchParsePollData(
          cache,
          data.bookingSourceGroups,
          BookingSourceGroup,
          this.bookingSourceGroups$
        )
      }

      this.commitPollPackage(
        cache,
        pkg,
        undefined,
        undefined,
        this.userPollState
      )
      if (pkg.done && data?.etag) {
        await this.putUserCursor(cache.userId, data.etag)
      }
    } catch (error) {
      cache.markFailed(error)
      this.userPollState.set(PollState.Error)
    }
  }

  /**
   * persists one presence poll package and its cursor in one transaction.
   */
  private async handlePresencePollPackage(
    cache: PresenceCache,
    pkg: PresencePollResponse
  ): Promise<void> {
    if (this.presenceCache !== cache || cache.hasFailed()) {
      return
    }

    try {
      const data = pkg.data

      await this.commitPollPage(
        cache,
        [
          {
            data: data?.presence ? [data.presence] : [],
            classIdentifier: Presence,
            observable: this.presences$,
          },
          { data: data?.sites, classIdentifier: Site, observable: this.sites$ },
          {
            data: data?.sessions,
            classIdentifier: Session,
            observable: this.sessions$,
            heavy: true,
          },
          { data: data?.users, classIdentifier: User, observable: this.users$ },
        ],
        PollKind.Presence,
        cache.presenceId,
        pkg.cursor,
        pkg.backfilling
      )

      if (data?.presence) {
        const polledPresence = Presence.getFactory().make(data.presence)
        if (cache.presenceId === polledPresence.id) {
          cache.setPresence(polledPresence)
        }
      }

      if (pkg.done) {
        cache.markOverviewReady()
      }

      this.commitPollPackage(
        cache,
        pkg,
        undefined,
        undefined,
        this.presencePollState
      )
    } catch (error) {
      cache.markFailed(error)
      this.presencePollState.set(PollState.Error)
    }
  }

  /**
   * persists one site poll package and its cursor in one transaction.
   */
  private async handleSitePollPackage(
    cache: SiteCache,
    pkg: SitePollResponse
  ): Promise<void> {
    if (this.siteCache !== cache || cache.hasFailed()) {
      return
    }

    try {
      const data = pkg.data

      await this.commitPollPage(
        cache,
        [
          {
            data: data?.site ? [data.site] : [],
            classIdentifier: Site,
            observable: this.sites$,
          },
          {
            data: data?.locations,
            classIdentifier: Location,
            observable: this.locations$,
          },
          {
            data: data?.columns,
            classIdentifier: Column,
            observable: this.columns$,
          },
          {
            data: data?.sessions,
            classIdentifier: Session,
            observable: this.sessions$,
            heavy: true,
          },
        ],
        PollKind.Site,
        cache.siteId,
        pkg.cursor,
        pkg.backfilling
      )

      if (data?.site) {
        const polledSite = Site.getFactory().make(data.site)
        if (cache.siteId === polledSite.id) {
          cache.setSite(polledSite)
        }
      }

      if (pkg.done) {
        cache.markOverviewReady()
      }

      this.commitPollPackage(
        cache,
        pkg,
        undefined,
        undefined,
        this.sitePollState
      )

      if (
        pkg.cursor &&
        !this.pollHeavyTargets.has(PollKind.Site + ':' + cache.siteId)
      ) {
        const site = await this.getActiveSite()
        await this.autoBackfill(
          PollKind.Site,
          cache.siteId,
          pkg.cursor,
          site?.created
        )
      }
    } catch (error) {
      cache.markFailed(error)
      this.sitePollState.set(PollState.Error)
    }
  }

  /**
   * persists one site log poll package and its cursor in one transaction.
   */
  private async handleSiteLogPollPackage(
    cache: SiteLogCache,
    pkg: SiteLogPollResponse
  ): Promise<void> {
    if (this.siteLogCache !== cache || cache.hasFailed()) {
      return
    }

    try {
      await this.commitPollPage(
        cache,
        [
          {
            data: pkg.data?.logEntries,
            classIdentifier: Entry,
            observable: this.logEntries$,
            heavy: true,
          },
        ],
        PollKind.SiteLog,
        cache.siteId,
        pkg.cursor,
        pkg.backfilling
      )

      if (pkg.done) {
        cache.markOverviewReady()
      }

      this.commitPollPackage(
        cache,
        pkg,
        undefined,
        undefined,
        this.siteLogPollState
      )

      if (
        pkg.cursor &&
        !this.pollHeavyTargets.has(PollKind.SiteLog + ':' + cache.siteId)
      ) {
        const site = await this.getActiveSite()
        if (site && site.id === cache.siteId) {
          await this.autoBackfill(
            PollKind.SiteLog,
            cache.siteId,
            pkg.cursor,
            site.created
          )
        }
      }
    } catch (error) {
      cache.markFailed(error)
      this.siteLogPollState.set(PollState.Error)
    }
  }

  /**
   * persists one location poll package and its cursor in one transaction.
   */
  private async handleLocationPollPackage(
    cache: LocationCache,
    pkg: LocationPollResponse
  ): Promise<void> {
    if (this.locationCache !== cache || cache.hasFailed()) {
      return
    }

    try {
      const data = pkg.data

      await this.commitPollPage(
        cache,
        [
          {
            data: data?.location ? [data.location] : [],
            classIdentifier: Location,
            observable: this.locations$,
          },
          {
            data: data?.columns,
            classIdentifier: Column,
            observable: this.columns$,
          },
          {
            data: data?.sessions,
            classIdentifier: Session,
            observable: this.sessions$,
            heavy: true,
          },
        ],
        PollKind.Location,
        cache.locationId,
        pkg.cursor,
        pkg.backfilling
      )

      if (data?.location) {
        const polledLocation = Location.getFactory().make(data.location)
        if (
          cache.siteId === polledLocation.siteId &&
          cache.locationId === polledLocation.id
        ) {
          cache.setLocation(polledLocation)
        }
      }

      if (pkg.done) {
        cache.markOverviewReady()
      }

      this.commitPollPackage(
        cache,
        pkg,
        undefined,
        undefined,
        this.locationPollState
      )

      if (
        pkg.cursor &&
        !this.pollHeavyTargets.has(PollKind.Location + ':' + cache.locationId)
      ) {
        const location = await this.getActiveLocation()
        await this.autoBackfill(
          PollKind.Location,
          cache.locationId,
          pkg.cursor,
          location?.created
        )
      }
    } catch (error) {
      cache.markFailed(error)
      this.locationPollState.set(PollState.Error)
    }
  }

  //#endregion

  //#region persistance

  public async putEntity<T>(entity: T, factory: Factory<T>): Promise<void> {
    await this.isReady()
    const stored = normalizeEntityForStorage(entity, factory)
    await new Promise<any>((resolve, reject) => {
      if (!this.db) {
        throw new Error('DB not initialized.')
      }
      try {
        const table = factory.getTableName()
        const os = this.db.transaction([table], 'readwrite').objectStore(table)
        const req = os.put(stored)

        req.onsuccess = event => {
          resolve(event)
        }

        req.onerror = event => {
          console.error('Error deleting database.', event)
          reject(event)
        }
      } catch (err) {
        console.log('Error saving entity in DB.', err)
        reject(err)
      }
    })
  }

  private async getEntity<T>(
    id: string,
    factory: Factory<T>,
    url: string,
    invalidate: boolean = false
  ): Promise<T> {
    await this.isReady()

    if (!id) {
      throw new Error('Invalid ID: ID must be provided')
    }

    if (invalidate) {
      const e: T = await this.getEntityFromApi(url, factory)
      await this.putEntity(e, factory)
      return new Promise<T>(resolve => resolve(e))
    }

    const self = this
    return new Promise<T>(function (resolve, reject) {
      if (self.db == null) {
        throw new Error('DB was not initialized.')
      }

      const table: string = factory.getTableName()
      const os = self.db.transaction([table], 'readonly').objectStore(table)

      const req = os.get(id)

      req.onerror = async function () {
        try {
          const e: T = await self.getEntityFromApi(url, factory)
          self.putEntity(e, factory)
          resolve(e)
        } catch (x) {
          reject(x)
        }
      }

      req.onsuccess = async function () {
        try {
          let e: T
          if (invalidate) {
            e = await self.getEntityFromApi(url, factory)
            self.putEntity(e, factory)
          } else {
            if (req.result) {
              e = factory.make(jsonFromStorage(req.result))
            } else {
              e = await self.getEntityFromApi(url, factory)
              self.putEntity(e, factory)
            }
          }
          resolve(e)
        } catch (x) {
          reject(x)
        }
      }
    })
  }

  public async getEntityFromApi<T>(
    url: string,
    factory: Factory<T>
  ): Promise<T> {
    const res = (await this.send('GET', url, undefined, this.cache)) as any

    if (!res.data) {
      throw new Error('Data not found.')
    }

    return factory.make(res.data)
  }

  public async getAllEntitiesFromApi<T>(factory: Factory<T>): Promise<T[]> {
    try {
      const tableName = factory.getTableName()
      let url: string

      switch (tableName) {
        case 'sites':
          url = Site.getUrl()
          break
        case 'locations':
          return []
        case 'columns':
          return []
        case 'sessions':
          return []
        case 'payments':
          return []
        case 'users':
          url = User.getUrl()
          break
        case 'countries':
          url = Country.getUrl()
          break
        case 'customers':
          url = Customer.getUrl()
          break
        case 'presences':
          url = Presence.getUrl()
          break
        default:
          return []
      }

      const res = (await this.send('GET', url, undefined, this.cache)) as any

      if (!res.data || !Array.isArray(res.data)) {
        throw new Error('Data not found or not an array.')
      }

      const entities = res.data.map((item: any) => factory.make(item))

      for (const entity of entities) {
        await this.putEntity(entity, factory)
      }

      return entities
    } catch (error) {
      return []
    }
  }

  public async getAllEntitiesFromDb<T>(factory: Factory<T>): Promise<T[]> {
    await this.isReady()

    const table: string = factory.getTableName()
    return new Promise<T[]>((resolve, reject) => {
      const res: T[] = []
      const os = this.db!.transaction([table], 'readonly').objectStore(table)
      const req = os.openCursor()

      req.onerror = async (event: any) => {
        reject(event)
      }

      req.onsuccess = () => {
        const cursor = req.result
        if (cursor) {
          const o = factory.make(jsonFromStorage(cursor.value))
          res.push(o)
          cursor.continue()
        } else {
          resolve(res)
        }
      }
    })
  }

  public async getAllEntitiesOptimized<T>(factory: Factory<T>): Promise<T[]> {
    await this.isReady()
    const table = factory.getTableName()

    return new Promise<T[]>((resolve, reject) => {
      const transaction = this.db!.transaction([table], 'readonly')
      const objectStore = transaction.objectStore(table)

      // Read in pages: one getAll over a large table can exceed the browser's
      // limit for a single serialized result (Firefox).
      const pageSize = 5000
      const results: T[] = []

      const readPage = (lastKey?: any) => {
        const request = objectStore.getAll(
          lastKey === undefined
            ? undefined
            : IDBKeyRange.lowerBound(lastKey, true),
          pageSize
        )

        request.onsuccess = async () => {
          try {
            for (const data of request.result) {
              results.push(factory.make(jsonFromStorage(data)))
            }

            if (request.result.length === pageSize) {
              readPage(request.result[request.result.length - 1].id)
              return
            }

            await finish()
          } catch (error) {
            console.error(
              `CacheService: Error creating entities from getAll result`,
              {
                table,
                error,
              }
            )
            reject(
              new Error(
                `Failed to create entities: ${error instanceof Error ? error.message : 'Unknown error'}`
              )
            )
          }
        }

        request.onerror = () => {
          console.error(`CacheService: Error in getAllEntitiesOptimized`, {
            table,
            error: request.error,
          })
          reject(request.error || new Error('Failed to retrieve entities'))
        }
      }

      // An empty store stays empty: the api has no list route for these
      // collections, so asking it only failed (GET /customers: 405).
      const finish = async () => {
        resolve(results)
      }

      readPage()
    })
  }

  /** Calls visit with the stored JSON of every row of the factory's table, read in pages, without making entities. */
  public async forEachStoredJson<T>(
    factory: Factory<T>,
    visit: (json: any) => void
  ): Promise<void> {
    await this.isReady()
    const table = factory.getTableName()

    return new Promise<void>((resolve, reject) => {
      const transaction = this.db!.transaction([table], 'readonly')
      const objectStore = transaction.objectStore(table)
      const pageSize = 5000

      const readPage = (lastKey?: any) => {
        const request = objectStore.getAll(
          lastKey === undefined
            ? undefined
            : IDBKeyRange.lowerBound(lastKey, true),
          pageSize
        )

        request.onsuccess = () => {
          try {
            for (const data of request.result) {
              visit(jsonFromStorage(data))
            }
          } catch (error) {
            reject(error)
            return
          }

          if (request.result.length === pageSize) {
            readPage(request.result[request.result.length - 1].id)
            return
          }

          resolve()
        }

        request.onerror = () => {
          console.error(`CacheService: Error in forEachStoredJson`, {
            table,
            error: request.error,
          })
          reject(request.error || new Error('Failed to retrieve entities'))
        }
      }

      readPage()
    })
  }

  public async getEntitiesBatched<T>(
    range: any,
    index: string,
    factory: Factory<T>,
    batchSize: number = 1000
  ): Promise<T[]> {
    await this.isReady()
    const table = factory.getTableName()

    return new Promise<T[]>((resolve, reject) => {
      const results: T[] = []
      let processed = 0
      let lastIndexKey: any = null
      let lastPrimaryKey: any = null

      const processBatch = () => {
        const transaction = this.db!.transaction([table], 'readonly')
        const objectStore = transaction.objectStore(table)
        const dbIndex = objectStore.index(index)

        let keyRange: IDBKeyRange | undefined
        if (
          range &&
          typeof range === 'object' &&
          'lower' in range &&
          'upper' in range
        ) {
          if (range.lower === range.upper) {
            keyRange = IDBKeyRange.only(range.lower)
          } else {
            keyRange = IDBKeyRange.bound(range.lower, range.upper, false, false)
          }
        } else if (range instanceof IDBKeyRange) {
          keyRange = range
        } else if (range !== null && range !== undefined) {
          keyRange = IDBKeyRange.only(range)
        }

        const request = dbIndex.openCursor(keyRange)

        request.onsuccess = () => {
          const cursor = request.result

          if (lastIndexKey !== null && lastPrimaryKey !== null && cursor) {
            const isPastLastItem =
              cursor.key > lastIndexKey ||
              (cursor.key === lastIndexKey &&
                cursor.primaryKey > lastPrimaryKey)

            if (!isPastLastItem) {
              cursor.continue()
              return
            }
          }

          if (cursor && processed < batchSize) {
            try {
              results.push(factory.make(jsonFromStorage(cursor.value)))
              lastIndexKey = cursor.key
              lastPrimaryKey = cursor.primaryKey
              processed++
              cursor.continue()
            } catch (error) {
              console.error(
                `CacheService: Error creating entity in batch processing`,
                {
                  table,
                  index,
                  cursorValue: cursor.value,
                  error,
                }
              )
              reject(
                new Error(
                  `Failed to create entity: ${error instanceof Error ? error.message : 'Unknown error'}`
                )
              )
            }
          } else {
            setTimeout(() => {
              if (cursor) {
                processed = 0
                processBatch()
              } else {
                resolve(results)
              }
            }, 0)
          }
        }

        request.onerror = () => {
          console.error(`CacheService: Error in getEntitiesBatched`, {
            table,
            index,
            range,
            error: request.error,
          })
          reject(
            request.error || new Error('Failed to retrieve entities in batch')
          )
        }
      }

      processBatch()
    })
  }

  public async getEntitiesPaginated<T>(
    range: any,
    index: string,
    factory: Factory<T>,
    page: number = 0,
    pageSize: number = 1000
  ): Promise<{
    data: T[]
    hasMore: boolean
    totalCount: number
    currentPage: number
    totalPages: number
  }> {
    await this.isReady()
    const table = factory.getTableName()

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([table], 'readonly')
      const objectStore = transaction.objectStore(table)
      const dbIndex = objectStore.index(index)

      let keyRange: IDBKeyRange | undefined
      if (
        range &&
        typeof range === 'object' &&
        'lower' in range &&
        'upper' in range
      ) {
        if (range.lower === range.upper) {
          keyRange = IDBKeyRange.only(range.lower)
        } else {
          keyRange = IDBKeyRange.bound(range.lower, range.upper, false, false)
        }
      } else if (range instanceof IDBKeyRange) {
        keyRange = range
      } else if (range !== null && range !== undefined) {
        keyRange = IDBKeyRange.only(range)
      }

      const countRequest = dbIndex.count(keyRange)
      countRequest.onsuccess = () => {
        const totalCount = countRequest.result
        const offset = page * pageSize
        const totalPages = Math.ceil(totalCount / pageSize)

        if (offset >= totalCount) {
          resolve({
            data: [],
            hasMore: false,
            totalCount,
            currentPage: page,
            totalPages,
          })
          return
        }

        const dataRequest = dbIndex.openCursor(keyRange)
        const results: T[] = []
        let currentIndex = 0

        dataRequest.onsuccess = () => {
          const cursor = dataRequest.result
          if (cursor) {
            if (currentIndex >= offset && results.length < pageSize) {
              try {
                results.push(factory.make(jsonFromStorage(cursor.value)))
              } catch (error) {
                console.error(
                  `CacheService: Error creating entity in pagination`,
                  {
                    table,
                    index,
                    cursorValue: cursor.value,
                    error,
                  }
                )
                reject(
                  new Error(
                    `Failed to create entity: ${error instanceof Error ? error.message : 'Unknown error'}`
                  )
                )
                return
              }
            }
            currentIndex++

            if (results.length < pageSize && currentIndex < totalCount) {
              cursor.continue()
            } else {
              resolve({
                data: results,
                hasMore: offset + results.length < totalCount,
                totalCount,
                currentPage: page,
                totalPages,
              })
            }
          } else {
            resolve({
              data: results,
              hasMore: false,
              totalCount,
              currentPage: page,
              totalPages,
            })
          }
        }

        dataRequest.onerror = () => {
          console.error(
            `CacheService: Error in getEntitiesPaginated data request`,
            {
              table,
              index,
              range,
              error: dataRequest.error,
            }
          )
          reject(
            dataRequest.error ||
              new Error('Failed to retrieve paginated entities')
          )
        }
      }

      countRequest.onerror = () => {
        console.error(
          `CacheService: Error in getEntitiesPaginated count request`,
          {
            table,
            index,
            range,
            error: countRequest.error,
          }
        )
        reject(countRequest.error || new Error('Failed to count entities'))
      }
    })
  }

  public async *getEntitiesAsync<T>(
    range: any,
    index: string,
    factory: Factory<T>,
    batchSize: number = 1000
  ): AsyncGenerator<T[], void, unknown> {
    await this.isReady()
    const table = factory.getTableName()

    let hasMore = true
    let lastKey: any = null

    while (hasMore) {
      const batch = await new Promise<T[]>((resolve, reject) => {
        const transaction = this.db!.transaction([table], 'readonly')
        const objectStore = transaction.objectStore(table)
        const dbIndex = objectStore.index(index)

        let keyRange: IDBKeyRange | undefined
        if (
          range &&
          typeof range === 'object' &&
          'lower' in range &&
          'upper' in range
        ) {
          if (range.lower === range.upper) {
            keyRange = IDBKeyRange.only(range.lower)
          } else {
            keyRange = IDBKeyRange.bound(range.lower, range.upper, false, false)
          }
        } else if (range instanceof IDBKeyRange) {
          keyRange = range
        } else if (range !== null && range !== undefined) {
          keyRange = IDBKeyRange.only(range)
        }

        const finalKeyRange = lastKey
          ? IDBKeyRange.lowerBound(lastKey, true)
          : keyRange

        const request = dbIndex.openCursor(finalKeyRange)
        const results: T[] = []
        let count = 0

        request.onsuccess = () => {
          const cursor = request.result
          if (cursor && count < batchSize) {
            try {
              results.push(factory.make(jsonFromStorage(cursor.value)))
              lastKey = cursor.key
              count++
              cursor.continue()
            } catch (error) {
              console.error(
                `CacheService: Error creating entity in async iterator`,
                {
                  table,
                  index,
                  cursorValue: cursor.value,
                  error,
                }
              )
              reject(
                new Error(
                  `Failed to create entity: ${error instanceof Error ? error.message : 'Unknown error'}`
                )
              )
            }
          } else {
            hasMore = count === batchSize
            resolve(results)
          }
        }

        request.onerror = () => {
          console.error(`CacheService: Error in getEntitiesAsync`, {
            table,
            index,
            range,
            error: request.error,
          })
          reject(
            request.error ||
              new Error('Failed to retrieve entities in async iterator')
          )
        }
      })

      if (batch.length > 0) {
        yield batch
      } else {
        hasMore = false
      }
    }
  }

  public async processLargeDataset<T>(
    range: any,
    index: string,
    factory: Factory<T>,
    processor: (batch: T[]) => Promise<void>,
    batchSize: number = 1000
  ): Promise<{ totalProcessed: number; batchesProcessed: number }> {
    let totalProcessed = 0
    let batchesProcessed = 0

    try {
      for await (const batch of this.getEntitiesAsync(
        range,
        index,
        factory,
        batchSize
      )) {
        await processor(batch)
        totalProcessed += batch.length
        batchesProcessed++
      }

      return { totalProcessed, batchesProcessed }
    } catch (error) {
      console.error(`CacheService: Error in processLargeDataset`, {
        range,
        index,
        error,
        totalProcessed,
        batchesProcessed,
      })
      throw error
    }
  }

  public async getSessionsOptimized(
    activeEntity: Entity,
    fromDate?: Date | string | null,
    untilDate?: Date | string | null,
    options: {
      usePagination?: boolean
      page?: number
      pageSize?: number
      useBatching?: boolean
      batchSize?: number
    } = {}
  ): Promise<
    | Session[]
    | {
        data: Session[]
        hasMore: boolean
        totalCount: number
        currentPage: number
        totalPages: number
      }
  > {
    let indexName: string
    let entityId: string

    if (activeEntity instanceof Customer) {
      indexName = 'customerId'
      entityId = activeEntity.id
    } else if (activeEntity instanceof Presence) {
      indexName = 'presenceId'
      entityId = activeEntity.id
    } else if (activeEntity instanceof Site) {
      indexName = 'siteId'
      entityId = activeEntity.id
    } else if (activeEntity instanceof Location) {
      indexName = 'locationId'
      entityId = activeEntity.id
    } else if (activeEntity instanceof Column) {
      indexName = 'columnId'
      entityId = activeEntity.id
    } else if (activeEntity instanceof Locker) {
      indexName = 'lockerId'
      entityId = activeEntity.id
    } else {
      return options.usePagination
        ? {
            data: [],
            hasMore: false,
            totalCount: 0,
            currentPage: 0,
            totalPages: 0,
          }
        : []
    }

    const range = { lower: entityId, upper: entityId }
    const from = fromDate ? new Date(fromDate) : new Date(0)
    const until = untilDate ? new Date(untilDate) : new Date('9999-12-31')

    if (options.usePagination) {
      const result = await this.getEntitiesPaginated(
        range,
        indexName,
        Session.getFactory(),
        options.page || 0,
        options.pageSize || 1000
      )

      const filteredData = result.data.filter(session => {
        const sessionDate = session.created
        return sessionDate >= from && sessionDate <= until
      })

      return {
        ...result,
        data: filteredData,
      }
    }

    if (options.useBatching) {
      const sessions = await this.getEntitiesBatched(
        range,
        indexName,
        Session.getFactory(),
        options.batchSize || 1000
      )

      return sessions.filter(session => {
        const sessionDate = session.created
        return sessionDate >= from && sessionDate <= until
      })
    }

    const sessions = await this.getEntitiesFromDbByIndex(
      range,
      indexName,
      Session.getFactory()
    )

    return sessions.filter(session => {
      const sessionDate = session.created
      return sessionDate >= from && sessionDate <= until
    })
  }

  public async getEntitiesFromDbByIndex<T>(
    range: any,
    index: string,
    factory: Factory<T>
  ): Promise<T[]> {
    const table = factory.getTableName()

    try {
      await this.isReady()

      if (!this.db) {
        throw new Error('Database is not initialized')
      }

      return new Promise<T[]>((resolve, reject) => {
        const res: T[] = []

        try {
          const transaction = this.db!.transaction([table], 'readonly')
          const objectStore = transaction.objectStore(table)

          if (!objectStore) {
            reject(new Error(`Object store '${table}' not found in database`))
            return
          }

          const dbIndex = objectStore.index(index)
          if (!dbIndex) {
            reject(
              new Error(`Index '${index}' not found on object store '${table}'`)
            )
            return
          }

          let keyRange: IDBKeyRange | undefined
          if (
            range &&
            typeof range === 'object' &&
            'lower' in range &&
            'upper' in range
          ) {
            if (range.lower === range.upper) {
              keyRange = IDBKeyRange.only(range.lower)
            } else {
              keyRange = IDBKeyRange.bound(
                range.lower,
                range.upper,
                false,
                false
              )
            }
          } else if (range instanceof IDBKeyRange) {
            keyRange = range
          } else if (range !== null && range !== undefined) {
            keyRange = IDBKeyRange.only(range)
          }

          const request = dbIndex.openCursor(keyRange)

          request.onerror = (event: Event) => {
            const target = event.target as IDBRequest
            const error = target.error

            let errorMessage = 'Failed to retrieve entities from database'

            if (error) {
              switch (error.name) {
                case 'InvalidStateError':
                  errorMessage = `Database operation failed: Invalid state for table '${table}' and index '${index}'`
                  break
                case 'DataError':
                  errorMessage = `Database operation failed: Invalid data for table '${table}' and index '${index}'`
                  break
                case 'ConstraintError':
                  errorMessage = `Database operation failed: Constraint violation for table '${table}' and index '${index}'`
                  break
                case 'QuotaExceededError':
                  errorMessage = `Database operation failed: Storage quota exceeded`
                  break
                case 'UnknownError':
                  errorMessage = `Database operation failed: Unknown error occurred`
                  break
                default:
                  errorMessage = `Database operation failed: ${error.message || error.name}`
              }
            }

            console.error(`CacheService: ${errorMessage}`, {
              table,
              index,
              range,
              error: error?.toString(),
              event,
            })

            reject(new Error(errorMessage))
          }

          request.onsuccess = () => {
            try {
              const cursor = request.result
              if (cursor) {
                try {
                  const entity = factory.make(jsonFromStorage(cursor.value))
                  res.push(entity)
                  cursor.continue()
                } catch (factoryError) {
                  console.error(
                    `CacheService: Failed to create entity from cursor value`,
                    {
                      table,
                      index,
                      cursorValue: cursor.value,
                      error: factoryError,
                    }
                  )
                  reject(
                    new Error(
                      `Failed to create entity from database data: ${factoryError instanceof Error ? factoryError.message : 'Unknown error'}`
                    )
                  )
                }
              } else {
                resolve(res)
              }
            } catch (successError) {
              console.error(`CacheService: Error in cursor success handler`, {
                table,
                index,
                error: successError,
              })
              reject(
                new Error(
                  `Error processing database cursor: ${successError instanceof Error ? successError.message : 'Unknown error'}`
                )
              )
            }
          }

          transaction.onerror = (event: Event) => {
            const target = event.target as IDBTransaction
            const error = target.error

            let errorMessage = 'Database transaction failed'

            if (error) {
              errorMessage = `Database transaction failed: ${error.message || error.name}`
            }

            console.error(`CacheService: ${errorMessage}`, {
              table,
              index,
              error: error?.toString(),
              event,
            })

            reject(new Error(errorMessage))
          }
        } catch (setupError) {
          console.error(`CacheService: Failed to setup database operation`, {
            table,
            index,
            error: setupError,
          })
          reject(
            new Error(
              `Failed to setup database operation: ${setupError instanceof Error ? setupError.message : 'Unknown error'}`
            )
          )
        }
      })
    } catch (error) {
      console.error(`CacheService: Error in getEntitiesFromDbByIndex`, {
        table: factory.getTableName(),
        index,
        range,
        error,
      })

      if (error instanceof Error) {
        throw error
      } else {
        throw new Error(
          `Unexpected error in getEntitiesFromDbByIndex: ${String(error)}`
        )
      }
    }
  }

  //#endregion

  //#region active
  async getActiveUser(): Promise<User | undefined> {
    const c = this.cache
    if (!c) {
      return undefined
    }

    if (!c.userId) {
      console.warn('CacheService: No userId found in cache')
      return undefined
    }

    if (c.user) {
      return c.user
    } else {
      const u = await this.getUser(c.userId)
      c.setUser(u)
      return u
    }
  }

  async getActiveLocation(): Promise<Location | undefined> {
    const c = this.locationCache
    const sc = this.siteCache
    if (!c || !sc) {
      return undefined
    }

    if (c.location) {
      return c.location
    } else {
      const location = await this.getLocation(sc.siteId, c.locationId)
      c.setLocation(location)
      return location
    }
  }

  async setActivePresence(presenceId: string) {
    const uc = this.cache
    if (!uc) {
      return
    }

    const state = this.presencePollState()
    const samePresence =
      this.presenceCache?.userId === uc.userId &&
      this.presenceCache?.presenceId === presenceId
    if (samePresence && isSettledOrActivePoll(state)) {
      return
    }

    const pc = new PresenceCache(uc.userId, presenceId)
    this.presenceCache = pc
    localStorage.setItem('presenceId', presenceId)

    this.presencePollState.set(PollState.Awaiting)
    this.requestPollLock(
      PollKind.Presence,
      presenceId,
      async () => {
        const cursor = await this.getPollCursor(PollKind.Presence, presenceId)
        if (this.presenceCache !== pc) {
          return
        }
        this.presencePoll.postMessage({
          type: PresencePollRequestType.Signin,
          apiUrl: this.apiUrl,
          acceptHeader: this.acceptHeader,
          accessToken: uc.accessToken,
          refreshToken: uc.refreshToken,
          userId: uc.userId,
          presenceId: presenceId,
          cursor: cursor,
          backfillTo: this.pollHeavyTargets.get(
            PollKind.Presence + ':' + presenceId
          ),
        })
      },
      () =>
        this.presencePoll.postMessage({
          type: PresencePollRequestType.Signout,
        })
    )
  }

  async setActiveSite(
    siteId: string,
    options?: { fetchFullHistory?: boolean }
  ) {
    const uc = this.cache
    if (!uc) {
      return
    }

    const state = this.sitePollState()
    const sameSite = this.siteCache?.siteId === siteId
    if (sameSite && isSettledOrActivePoll(state)) {
      if (options?.fetchFullHistory) {
        await this.pollHeavyBackTo(
          PollKind.Site,
          siteId,
          new Date('2020-01-01T00:00:00Z')
        )
      }
      return
    }

    const sc = new SiteCache(siteId)
    this.siteCache = sc
    localStorage.setItem('siteId', siteId)

    this.sitePollState.set(PollState.Awaiting)
    this.requestPollLock(
      PollKind.Site,
      siteId,
      async () => {
        const cursor = await this.getPollCursor(PollKind.Site, siteId)
        if (this.siteCache !== sc) {
          return
        }
        this.sitePoll.postMessage({
          type: SitePollRequestType.Signin,
          apiUrl: this.apiUrl,
          acceptHeader: this.acceptHeader,
          accessToken: uc.accessToken,
          refreshToken: uc.refreshToken,
          userId: uc.userId,
          siteId: siteId,
          cursor: cursor,
          backfillTo: this.pollHeavyTargets.get(PollKind.Site + ':' + siteId),
        })
      },
      () => this.sitePoll.postMessage({ type: SitePollRequestType.Signout })
    )

    if (options?.fetchFullHistory) {
      await this.pollHeavyBackTo(
        PollKind.Site,
        siteId,
        new Date('2020-01-01T00:00:00Z')
      )
    }
  }

  async getActiveSite(): Promise<Site | undefined> {
    const c = this.siteCache
    if (!c) {
      return undefined
    }

    if (c.site) {
      return c.site
    } else {
      const site = await this.getSite(c.siteId)
      c.setSite(site)
      return site
    }
  }

  async setActiveLocation(locationId: string) {
    const uc = this.cache
    const sc = this.siteCache
    if (!uc || !sc) {
      return
    }

    const state = this.locationPollState()
    const sameLocation =
      this.locationCache?.siteId === sc.siteId &&
      this.locationCache?.locationId === locationId
    if (sameLocation && isSettledOrActivePoll(state)) {
      return
    }

    const lc = new LocationCache(sc.siteId, locationId)
    this.locationCache = lc
    localStorage.setItem('locationId', locationId)

    this.locationPollState.set(PollState.Awaiting)
    this.requestPollLock(
      PollKind.Location,
      locationId,
      async () => {
        const cursor = await this.getPollCursor(PollKind.Location, locationId)
        if (this.locationCache !== lc) {
          return
        }
        this.locationPoll.postMessage({
          type: LocationPollRequestType.Signin,
          apiUrl: this.apiUrl,
          acceptHeader: this.acceptHeader,
          accessToken: uc.accessToken,
          refreshToken: uc.refreshToken,
          siteId: sc.siteId,
          userId: uc.userId,
          locationId: locationId,
          cursor: cursor,
          backfillTo: this.pollHeavyTargets.get(
            PollKind.Location + ':' + locationId
          ),
        })
      },
      () =>
        this.locationPoll.postMessage({
          type: LocationPollRequestType.Signout,
        })
    )
  }

  async getActivePresence(): Promise<Presence | undefined> {
    const c = this.presenceCache
    if (!c) {
      return undefined
    }

    if (c.presence) {
      return c.presence
    } else {
      const location = await this.getPresence(c.presenceId)
      c.setPresence(location)
      return location
    }
  }

  async setActiveColumn(columnId: string, siteId?: string) {
    const effectiveSiteId = siteId ?? this.siteCache?.siteId
    if (!effectiveSiteId) {
      return
    }

    try {
      const column = await this.getColumn(effectiveSiteId, columnId)
      this.columnCache = new ColumnCache()
      this.columnCache.setColumn(column)
      localStorage.setItem('columnId', columnId)
    } catch (error: unknown) {
      if (error instanceof Fault) {
        throw new Error(
          `Error setting columnCache for site=${effectiveSiteId}, column=${columnId}: ${error.code} - ${error.text}`
        )
      }
      throw new Error(
        `Error setting columnCache for site=${effectiveSiteId}, column=${columnId}: ${error}`
      )
    }
  }

  async getActiveColumn(): Promise<Column | undefined> {
    const c = this.columnCache
    if (c && c.column) {
      return c.column
    } else {
      return undefined
    }
  }

  async updateActiveColumn(id: string): Promise<Column | undefined> {
    if (id === this.columnCache?.column?.id) {
      await this.siteHasPolled()
      try {
        const column = await this.getColumn(this.columnCache.column.siteId, id)
        this.columnCache.setColumn(column)
        return column
      } catch (error: unknown) {
        throw new Error(`Error updating columnCache: ${error}`)
      }
    }

    return undefined
  }

  async getActiveCustomer(): Promise<Customer | undefined> {
    const c = this.customerCache
    if (!c) {
      return undefined
    }

    if (c.customer) {
      return c.customer
    } else {
      const customer = await this.getCustomer(c.customerId)
      c.setCustomer(customer)
      return customer
    }
  }

  async setActiveCustomer(customerId: string) {
    const uc = this.cache
    if (uc) {
      try {
        this.customerCache = new CustomerCache(uc.userId, customerId)
        localStorage.setItem('customerId', customerId)
      } catch (error: unknown) {
        console.error('CacheService: Error setting customerCache:', error)
        throw new Error(`Error setting customerCache: ${error}`)
      }
    }
  }

  async setActiveCountry(countryId: string) {
    try {
      const country = await this.getCountry(countryId)
      this.countryCache = new CountryCache()
      this.countryCache.setCountry(country)
      localStorage.setItem('countryId', countryId)
    } catch (error: unknown) {
      throw new Error(`Error setting countryCache: ${error}`)
    }
  }

  async getActiveCountry(): Promise<Country | undefined> {
    const c = this.countryCache
    if (c && c.country) {
      return c.country
    } else {
      return undefined
    }
  }

  async setActiveLocker(lockerId: string) {
    const c = this.columnCache
    if (!c) {
      this.lockerCache = undefined
      return console.error(
        'Unable to set locker cache due to no active column.'
      )
    }

    const locker = c.column?.lockers.find(locker => locker.id === lockerId)
    if (!locker) {
      this.lockerCache = undefined
      return console.error(
        'Unable to set locker cache due to being unable to locate locker on column.'
      )
    }

    try {
      this.lockerCache = new LockerCache()
      this.lockerCache.setLocker(locker)
      localStorage.setItem('lockerId', lockerId)
    } catch (x: any) {
      throw new Error('Error setting lockerCache', x)
    }
  }

  async getActiveLocker(): Promise<Locker | undefined> {
    const c = this.lockerCache
    if (c && c.locker) {
      return c.locker
    } else {
      return undefined
    }
  }

  async updateActiveLocker(id: string): Promise<Locker | undefined> {
    if (id === this.lockerCache?.locker?.id) {
      await this.siteHasPolled()
      const column = await this.getActiveColumn()
      if (column) {
        const locker = await column.getLocker(id)
        if (locker) {
          try {
            this.lockerCache.setLocker(locker)
            return locker
          } catch (error: unknown) {
            throw new Error(`Error updating lockerCache: ${error}`)
          }
        }
      }
    }

    return undefined
  }

  //#endregion

  //#region get
  async getSpotlight(
    request: SpotlightRequest
  ): Promise<
    | SpotlightUserResponse
    | SpotlightColumnSessionsResponse
    | SpotlightSitesAndLocationsResponse
  > {
    await this.isReady()
    const url = '/sessions/spotlight'

    if (!(request instanceof SpotlightRequest)) {
      throw new Error('Invalid request type.')
    }

    if (!this.cache) {
      throw new Error('Cache was not initialized.')
    }

    const res = (await this.send(
      'POST',
      url,
      { data: request },
      this.cache
    )) as DataResponse
    if (!res.data) {
      throw new Error('Data not found.')
    }

    let output:
      | SpotlightUserResponse
      | SpotlightColumnSessionsResponse
      | SpotlightSitesAndLocationsResponse
    if (
      request instanceof SpotlightEmailRequest ||
      request instanceof SpotlightPhoneRequest
    ) {
      output = new SpotlightUserResponse(res.data)
      for (let session of output.data) {
        const s = Session.getFactory().make(session)
        this.putEntity(s, Session.getFactory())
        await this.sessions$.next(s)
      }
      await this.sessions$.next(new SubscriberSentinel())
    } else if (request instanceof SpotlightColumnRequest) {
      output = new SpotlightColumnSessionsResponse(res.data)

      if (Array.isArray(output.data)) {
        for (let item of output.data) {
          const column = Column.getFactory().make(item.column)
          this.putEntity(column, Column.getFactory())
          await this.columns$.next(column)

          for (let lockerSession of item.lockerSessions || []) {
            for (let session of lockerSession.sessions || []) {
              const s = Session.getFactory().make(session)
              this.putEntity(s, Session.getFactory())
              await this.sessions$.next(s)
            }
          }
        }
      } else {
        const column = Column.getFactory().make(output.data.column)
        this.putEntity(column, Column.getFactory())
        await this.columns$.next(column)

        for (let lockerSession of output.data.sessions || []) {
          for (let session of lockerSession.sessions || []) {
            const s = Session.getFactory().make(session)
            this.putEntity(s, Session.getFactory())
            await this.sessions$.next(s)
          }
        }
      }

      await this.columns$.next(new SubscriberSentinel())
      await this.sessions$.next(new SubscriberSentinel())
    } else if (request instanceof SpotlightSitesAndLocationsRequest) {
      output = new SpotlightSitesAndLocationsResponse(res.data, res.extra)
    } else {
      throw new Error('Invalid request type.')
    }

    return output
  }

  async getPayment(
    userId: string,
    paymentId: string,
    invalidate: boolean = false
  ): Promise<Payment> {
    if (!userId || !paymentId) {
      throw new Error('User ID and Payment ID must be provided.')
    }

    await this.isReady()
    const factory = Payment.getFactory()
    const url = `/users/${userId}/payments/${paymentId}`

    return this.getEntity(paymentId, factory, url, invalidate)
  }

  async getBatchedPayments(data: [string, string][]): Promise<Payment[]> {
    if (!Array.isArray(data) || data.length === 0) {
      return []
    }

    for (const [userId, paymentId] of data) {
      if (!userId || !paymentId) {
        throw new Error(
          'Invalid data format: userId and paymentId are required'
        )
      }
    }

    if (!this.cache) {
      throw new Error('Cache was not initialized.')
    }

    await this.isReady()
    const factory = Payment.getFactory()

    const existingPayments: Payment[] = []
    const missingPaymentIds: string[] = []

    for (const [_, paymentId] of data) {
      try {
        const existingPayment = await this.getPaymentFromDb(paymentId, factory)
        if (existingPayment) {
          existingPayments.push(existingPayment)
        } else {
          missingPaymentIds.push(paymentId)
        }
      } catch (error) {
        missingPaymentIds.push(paymentId)
      }
    }

    if (missingPaymentIds.length === 0) {
      return existingPayments
    }

    const missingData = data.filter(([_, paymentId]) =>
      missingPaymentIds.includes(paymentId)
    )

    const batchSize = 2500
    const allNewPayments: Payment[] = []
    const totalBatches = Math.ceil(missingData.length / batchSize)

    for (let i = 0; i < missingData.length; i += batchSize) {
      const batchNumber = Math.floor(i / batchSize) + 1
      const batch = missingData.slice(i, i + batchSize)

      try {
        const url = `/payments/batch`

        const res = (await this.send(
          'POST',
          url,
          {
            data: batch,
          },
          this.cache
        )) as DataResponse

        if (!res.data) {
          throw new Error(`Batch ${batchNumber}: Data not found.`)
        }

        const newPayments: Payment[] = res.data.map((payment: Payment) => {
          const paymentObj = factory.make(payment)
          this.putEntity(paymentObj, factory).catch(error => {
            console.warn(
              `[getBatchedPayments] Failed to store payment ${paymentObj.id} in IndexedDB:`,
              error
            )
          })
          return paymentObj
        })

        allNewPayments.push(...newPayments)
      } catch (error) {
        console.error(
          `[getBatchedPayments] Error processing batch ${batchNumber}/${totalBatches}:`,
          error
        )
        console.warn(
          `[getBatchedPayments] Skipping batch ${batchNumber}, continuing with remaining batches`
        )
      }
    }

    const totalPayments = [...existingPayments, ...allNewPayments]
    console.log(
      `📊 [CACHE-SERVICE] Retrieved ${totalPayments.length} payments (${existingPayments.length} cached, ${allNewPayments.length} fetched)`
    )
    return totalPayments
  }

  private async getPaymentFromDb(
    paymentId: string,
    factory: Factory<Payment>
  ): Promise<Payment | null> {
    return new Promise<Payment | null>((resolve, reject) => {
      if (!this.db) {
        reject(new Error('DB was not initialized.'))
        return
      }

      const table = factory.getTableName()
      const os = this.db.transaction([table], 'readonly').objectStore(table)
      const req = os.get(paymentId)

      req.onerror = () => {
        reject(req.error)
      }

      req.onsuccess = () => {
        if (req.result) {
          resolve(factory.make(jsonFromStorage(req.result)))
        } else {
          resolve(null)
        }
      }
    })
  }

  async getUser(id: string, invalidate: boolean = false): Promise<User> {
    if (!id) {
      throw new Error('Invalid ID: ID must be provided')
    }

    return this.getEntity(id, User.getFactory(), User.getUrl(id), invalidate)
  }

  async getCustomer(
    id: string,
    invalidate: boolean = false
  ): Promise<Customer> {
    const customer = await this.getEntity(
      id,
      Customer.getFactory(),
      Customer.getUrl(id),
      invalidate
    )

    return customer
  }

  async getSite(id: string, invalidate: boolean = false): Promise<Site> {
    if (!id) {
      throw new Error('Invalid ID: ID must be provided')
    }

    return this.getEntity(id, Site.getFactory(), Site.getUrl(id), invalidate)
  }

  async getSites(): Promise<Site[]> {
    const factory = Site.getFactory()

    try {
      const db = await this.getDb()
      const transaction = db.transaction(['sites'], 'readonly')
      const store = transaction.objectStore('sites')
      const request = store.getAll()

      return new Promise((resolve, reject) => {
        request.onsuccess = () => {
          const results = request.result
          if (results && results.length > 0) {
            resolve(
              results.map((site: any) => factory.make(jsonFromStorage(site)))
            )
          } else {
            resolve([])
          }
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.warn('Failed to get sites from cache:', error)
      return []
    }
  }

  async getBookingSourceGroups(): Promise<BookingSourceGroup[]> {
    const factory = BookingSourceGroup.getFactory()

    try {
      await this.isReady()
      const transaction = this.db!.transaction(
        ['booking_source_groups'],
        'readonly'
      )
      const store = transaction.objectStore('booking_source_groups')
      const request = store.getAll()

      return new Promise((resolve, reject) => {
        request.onsuccess = () => {
          const results = request.result
          if (results && results.length > 0) {
            resolve(
              results.map((group: any) => factory.make(jsonFromStorage(group)))
            )
          } else {
            resolve([])
          }
        }
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.warn('Failed to get booking source groups from cache:', error)
      return []
    }
  }

  async getBookingSourceGroup(
    id: string,
    invalidate: boolean = false
  ): Promise<BookingSourceGroup> {
    return this.getEntity(
      id,
      BookingSourceGroup.getFactory(),
      BookingSourceGroup.getUrl(id),
      invalidate
    )
  }

  async getSiteLocations(site: Site): Promise<Location[]> {
    if (!site) {
      throw new Error('Site must be present.')
    }

    const res = (await this.send(
      'GET',
      Site.getUrl(site.id) + '/locations',
      undefined,
      this.cache
    )) as DataResponse

    if (!res.data) {
      throw new Error('Data not found.')
    }

    const f = Location.getFactory()
    const o: Location[] = res.data.map((location: Location) => f.make(location))
    return o
  }

  async getColumn(
    siteId: string,
    columnId: string,
    invalidate: boolean = false
  ): Promise<Column> {
    if (!columnId) {
      throw new Error('Invalid ID: ID must be provided')
    }

    return this.getEntity(
      columnId,
      Column.getFactory(),
      Column.getUrl(siteId, columnId),
      invalidate
    )
  }

  async getLocation(
    siteId: string,
    id: string,
    invalidate: boolean = false
  ): Promise<Location> {
    if (!id) {
      throw new Error('Invalid ID: ID must be provided')
    }
    return this.getEntity(
      id,
      Location.getFactory(),
      Location.getUrl(siteId, id),
      invalidate
    )
  }

  async getCountry(id: string, invalidate: boolean = false): Promise<Country> {
    if (!id) {
      throw new Error('Invalid ID: ID must be provided')
    }
    return this.getEntity(
      id,
      Country.getFactory(),
      Country.getUrl(id),
      invalidate
    )
  }

  async getPresence(
    id: string,
    invalidate: boolean = false
  ): Promise<Presence> {
    if (!id) {
      throw new Error('Invalid ID: ID must be provided')
    }
    return this.getEntity(
      id,
      Presence.getFactory(),
      Presence.getUrl(id),
      invalidate
    )
  }

  async getLockerDimensions(
    id: string,
    invalidate: boolean = false
  ): Promise<Dimensions> {
    return this.getEntity(
      id,
      Dimensions.getFactory(),
      Dimensions.getUrl(id),
      invalidate
    )
  }

  async getLocker(id: string, invalidate: boolean = false): Promise<Locker> {
    return this.getEntity(
      id,
      Locker.getFactory(),
      Locker.getUrl(id),
      invalidate
    )
  }

  getSessionsByUserId(userId: string): Promise<Session[]> {
    return this.getEntitiesFromDbByIndex(
      { lower: userId, upper: userId },
      'userId',
      Session.getFactory()
    )
  }

  async getLatestSessionByLockerId(
    lockerId: string
  ): Promise<Session | undefined> {
    const sessions = await this.getEntitiesFromDbByIndex(
      { lower: lockerId, upper: lockerId },
      'lockerId',
      Session.getFactory()
    )
    sessions.sort((a, b) => b.created.getTime() - a.created.getTime())
    return sessions.length > 0 ? sessions[0] : undefined
  }

  getLogEntriesBySessionId(sessionId: string): Promise<Entry[]> {
    return this.getEntitiesFromDbByIndex(
      { lower: sessionId, upper: sessionId },
      'sessionId',
      Entry.getFactory()
    )
  }

  async getLogEntriesByColumnId(columnId: string): Promise<Entry[]> {
    const allEntries = await this.getAllEntitiesOptimized(Entry.getFactory())
    return allEntries.filter(entry => {
      const anyEntry = entry as any
      return anyEntry?.columnId === columnId
    })
  }

  getLogEntriesByUserId(userId: string): Promise<Entry[]> {
    return this.getEntitiesFromDbByIndex(
      { lower: userId, upper: userId },
      'userId',
      Entry.getFactory()
    )
  }

  getLogEntriesBySourceId(sourceId: string): Promise<Entry[]> {
    return this.getEntitiesFromDbByIndex(
      { lower: sourceId, upper: sourceId },
      'sourceId',
      Entry.getFactory()
    )
  }

  getAllLogEntries(): Promise<Entry[]> {
    return this.getAllEntitiesFromDb(Entry.getFactory())
  }

  getAllUsers(): Promise<User[]> {
    return this.getAllEntitiesFromDb(User.getFactory())
  }

  getPresencesByCustomerId(customerId: string): Promise<Presence[]> {
    return this.getEntitiesFromDbByIndex(
      { lower: customerId, upper: customerId },
      'customerId',
      Presence.getFactory()
    )
  }

  getPresencesByCountryCode(countryCode: string): Promise<Presence[]> {
    return this.getEntitiesFromDbByIndex(
      { lower: countryCode, upper: countryCode },
      'countryCode',
      Presence.getFactory()
    )
  }

  getPresencesByDateRange(
    fromDate?: Date | string | null,
    untilDate?: Date | string | null
  ): Promise<Presence[]> {
    const from = fromDate ? new Date(fromDate) : new Date(0)
    const until = untilDate ? new Date(untilDate) : new Date('9999-12-31')

    const range = IDBKeyRange.bound(from, until, false, false)

    return this.getEntitiesFromDbByIndex(
      range,
      'created',
      Presence.getFactory()
    )
  }

  getPresencesByModifiedRange(
    fromDate?: Date | string | null,
    untilDate?: Date | string | null
  ): Promise<Presence[]> {
    const from = fromDate ? new Date(fromDate) : new Date(0)
    const until = untilDate ? new Date(untilDate) : new Date('9999-12-31')

    const range = IDBKeyRange.bound(from, until, false, false)

    return this.getEntitiesFromDbByIndex(
      range,
      'modified',
      Presence.getFactory()
    )
  }

  getPaymentsByUserId(userId: string): Promise<Payment[]> {
    return this.getEntitiesFromDbByIndex(
      { lower: userId, upper: userId },
      'userId',
      Payment.getFactory()
    )
  }

  getPaymentsByCurrency(currency: Currency): Promise<Payment[]> {
    return this.getEntitiesFromDbByIndex(
      { lower: currency, upper: currency },
      'currency',
      Payment.getFactory()
    )
  }

  getPaymentsByDateRange(
    fromDate?: Date | string | null,
    untilDate?: Date | string | null
  ): Promise<Payment[]> {
    const from = fromDate ? new Date(fromDate) : new Date(0)
    const until = untilDate ? new Date(untilDate) : new Date('9999-12-31')

    const range = IDBKeyRange.bound(from, until, false, false)

    return this.getEntitiesFromDbByIndex(range, 'created', Payment.getFactory())
  }

  getPaymentsByModifiedRange(
    fromDate?: Date | string | null,
    untilDate?: Date | string | null
  ): Promise<Payment[]> {
    const from = fromDate ? new Date(fromDate) : new Date(0)
    const until = untilDate ? new Date(untilDate) : new Date('9999-12-31')

    const range = IDBKeyRange.bound(from, until, false, false)

    return this.getEntitiesFromDbByIndex(
      range,
      'modified',
      Payment.getFactory()
    )
  }

  getSitesByPresenceId(presenceId: string): Promise<Site[]> {
    return this.getEntitiesFromDbByIndex(
      { lower: presenceId, upper: presenceId },
      'presenceId',
      Site.getFactory()
    )
  }

  getLocationsBySiteId(siteId: string): Promise<Location[]> {
    return this.getEntitiesFromDbByIndex(
      { lower: siteId, upper: siteId },
      'siteId',
      Location.getFactory()
    )
  }

  getColumnsByLocationId(locationId: string): Promise<Column[]> {
    return this.getEntitiesFromDbByIndex(
      { lower: locationId, upper: locationId },
      'locationId',
      Column.getFactory()
    )
  }

  getColumnsBySiteId(siteId: string): Promise<Column[]> {
    return this.getEntitiesFromDbByIndex(
      { lower: siteId, upper: siteId },
      'siteId',
      Column.getFactory()
    )
  }

  getColumnsByPresenceId(presenceId: string): Promise<Column[]> {
    return this.getEntitiesFromDbByIndex(
      { lower: presenceId, upper: presenceId },
      'presenceId',
      Column.getFactory()
    )
  }

  async getLockersByColumnId(columnId: string): Promise<Locker[]> {
    const column = await this.getColumn(columnId, columnId, false)
    return column?.lockers || []
  }

  private async add<T extends Identifiable>(
    factory: Factory<T>,
    o: T,
    url: string,
    observable?: AwaitingSubject<T>,
    extra?: any
  ): Promise<T> {
    if (!o) {
      throw new Error('Object must be present.')
    }

    await this.isReady()

    const cache = this.cache
    if (!cache) {
      throw new Error('Cannot add an object without being signed in.')
    }

    const body = extra ? { data: o, extra: extra } : { data: o }

    const res = (await this.send('POST', url, body, cache)) as DataResponse
    if (!res.data) {
      throw new Error('Data not found.')
    }

    o = factory.make(res.data)
    await this.putEntity(o, factory)

    if (observable) {
      await observable.next(o)
      await observable.next(new SubscriberSentinel())
    }

    return o
  }

  async addUser(user: User, extra?: any): Promise<User> {
    if (!user) {
      throw new Error('User must be present.')
    }

    await this.isReady()

    const cache = this.cache
    if (!cache) {
      throw new Error('Cannot add an object without being signed in.')
    }

    const body = extra ? { data: user, extra: extra } : { data: user }

    const res = (await this.send(
      'POST',
      User.getUrl(),
      body,
      cache
    )) as DataResponse
    if (!res.data) {
      throw new Error('Data not found.')
    }

    const userId = (res.data as any).userId
    if (!userId) {
      throw new Error(
        'User id was not returned by the server, unable to proceed but user was created.'
      )
    }

    user = User.getFactory().make(saveBody(user, { id: userId }))

    await this.putEntity(user, User.getFactory())
    await this.users$.next(user)
    await this.users$.next(new SubscriberSentinel())

    return user
  }

  async addCustomer(customer: Customer): Promise<Customer> {
    return await this.add(
      Customer.getFactory(),
      customer,
      Customer.getUrl(),
      this.customers$
    )
  }

  async addCountry(country: Country): Promise<Country> {
    return await this.add(
      Country.getFactory(),
      country,
      Country.getUrl(),
      this.countries$
    )
  }

  async addPresence(presence: Presence): Promise<Presence> {
    return await this.add(
      Presence.getFactory(),
      presence,
      Presence.getUrl(),
      this.presences$
    )
  }

  async addSite(site: Site): Promise<Site> {
    return await this.add(Site.getFactory(), site, Site.getUrl(), this.sites$)
  }

  async addLocation(location: Location): Promise<Location> {
    return await this.add(
      Location.getFactory(),
      location,
      Location.getUrl(location.siteId),
      this.locations$
    )
  }

  async addColumn(column: Column, siteId: string): Promise<Column> {
    return await this.add(
      Column.getFactory(),
      column,
      Column.getUrl(siteId),
      this.columns$
    )
  }

  async addLocker(
    locker: Locker,
    priceCategory: PriceCategory
  ): Promise<Locker> {
    return await this.add(
      Locker.getFactory(),
      locker,
      Locker.getUrl(locker.siteId),
      this.lockers$,
      priceCategory
    )
  }

  async addBookingSourceGroup(
    bookingSourceGroup: BookingSourceGroup
  ): Promise<BookingSourceGroup> {
    if (!bookingSourceGroup.nodes || bookingSourceGroup.nodes.length === 0) {
      throw new Error('Group must contain at least one node')
    }

    if (!bookingSourceGroup.names || bookingSourceGroup.names.length === 0) {
      throw new Error('Provide at least one name')
    }

    const activeUser = await this.getActiveUser()
    if (!activeUser) {
      throw new Error('User must be signed in to create booking source groups')
    }

    if (!activeUser.hasRole([Scope.GLOBAL_ADMIN])) {
      throw new Error(
        'User must have GLOBAL_ADMIN scope to create booking source groups'
      )
    }

    return await this.add(
      BookingSourceGroup.getFactory(),
      bookingSourceGroup,
      BookingSourceGroup.getUrl(),
      this.bookingSourceGroups$
    )
  }

  async addLockerDimensions(d: Dimensions): Promise<Dimensions> {
    return await this.add(
      Dimensions.getFactory(),
      d,
      Dimensions.getUrl(),
      undefined
    )
  }

  async addLockerPriceCategory(p: PriceCategory): Promise<PriceCategory> {
    return await this.add(
      PriceCategory.getFactory(),
      p,
      PriceCategory.getUrl(),
      undefined
    )
  }

  async addSession(session: Session, currency: Currency): Promise<Session> {
    const c = this.cache
    if (!session) {
      throw new Error('Session must be present.')
    }

    if (!c) {
      throw new Error("Can't add session without being signed in")
    }

    const res = (await this.send(
      'POST',
      Session.getUrl(session.siteId, session.lockerId),
      { data: currency },
      c
    )) as DataResponse

    if (!res.data) {
      throw new Error('Data not found.')
    }

    const f = Session.getFactory()
    const o = f.make(res.data)
    return o
  }

  async notifySessionHolders(messageRequest: MessageRequest) {
    const c = this.cache
    if (!c) {
      throw new Error("Can't notify session holder without being signed in")
    }

    await this.send(
      'POST',
      messageRequest.getUrl(),
      { data: messageRequest },
      c
    )
  }
  //#endregion

  //#region put

  private async edit<T extends Identifiable>(
    factory: Factory<T>,
    o: T,
    url: string,
    observable?: AwaitingSubject<T>,
    edits?: Record<string, unknown>
  ): Promise<T> {
    if (!o) {
      console.error('CacheService: Object must be present')
      throw new Error('Object must be present.')
    }

    const cache = this.cache
    if (!cache) {
      console.error('CacheService: Cannot edit without being signed in')
      throw new Error('Cannot edit an object without being signed in.')
    }

    await this.isReady()

    const data = saveBody(o, edits)

    const res = (await this.send('PUT', url, { data }, cache)) as DataResponse
    if (!res.data) {
      console.error('CacheService: No data in response')
      throw new Error('Data not found.')
    }

    o = factory.make(res.data)

    await this.putEntity(o, factory)

    if (observable) {
      await observable.next(o)
      await observable.next(new SubscriberSentinel())
    }

    return o
  }

  async editCustomer(
    customer: Customer,
    edits?: Record<string, unknown>
  ): Promise<Customer> {
    const updated = await this.edit(
      Customer.getFactory(),
      customer,
      Customer.getUrl(customer.id),
      this.customers$,
      edits
    )
    if (this.customerCache?.customerId === updated.id) {
      this.customerCache.setCustomer(updated)
    }
    this.notifyNavigationLabelsChanged()
    return updated
  }

  async editCountry(
    country: Country,
    edits?: Record<string, unknown>
  ): Promise<Country> {
    const updated = await this.edit(
      Country.getFactory(),
      country,
      Country.getUrl(country.id),
      this.countries$,
      edits
    )
    if (this.countryCache?.country?.id === updated.id) {
      this.countryCache.setCountry(updated)
    }
    this.notifyNavigationLabelsChanged()
    return updated
  }

  async editPresence(
    presence: Presence,
    edits?: Record<string, unknown>
  ): Promise<Presence> {
    const updated = await this.edit(
      Presence.getFactory(),
      presence,
      Presence.getUrl(presence.id),
      this.presences$,
      edits
    )
    if (this.presenceCache?.presenceId === updated.id) {
      this.presenceCache.setPresence(updated)
    }
    this.notifyNavigationLabelsChanged()
    return updated
  }

  async editSite(site: Site, edits?: Record<string, unknown>): Promise<Site> {
    const updated = await this.edit(
      Site.getFactory(),
      site,
      Site.getUrl(site.id),
      this.sites$,
      edits
    )
    if (this.siteCache?.siteId === updated.id) {
      this.siteCache.setSite(updated)
    }
    this.notifyNavigationLabelsChanged()
    return updated
  }

  async editLocation(
    location: Location,
    edits?: Record<string, unknown>
  ): Promise<Location> {
    const updated = await this.edit(
      Location.getFactory(),
      location,
      Location.getUrl(location.siteId, location.id),
      this.locations$,
      edits
    )
    if (
      this.locationCache?.siteId === updated.siteId &&
      this.locationCache?.locationId === updated.id
    ) {
      this.locationCache.setLocation(updated)
    }
    this.notifyNavigationLabelsChanged()
    return updated
  }

  async editColumn(
    column: Column,
    edits?: Record<string, unknown>
  ): Promise<Column> {
    const updated = await this.edit(
      Column.getFactory(),
      column,
      Column.getUrl(column.siteId, column.id),
      this.columns$,
      edits
    )
    this.syncActiveColumnAndNotifyNavigation(updated)
    return updated
  }

  async editLocker(
    locker: Locker,
    edits?: Record<string, unknown>
  ): Promise<Locker> {
    return await this.edit(
      Locker.getFactory(),
      locker,
      Locker.getUrl(locker.siteId, locker.id),
      this.lockers$,
      edits
    )
  }

  async editLockerDimensions(
    d: Dimensions,
    edits?: Record<string, unknown>
  ): Promise<Dimensions> {
    return await this.edit(
      Dimensions.getFactory(),
      d,
      Dimensions.getUrl(d.id),
      undefined,
      edits
    )
  }

  async editLockerPriceCategory(
    p: PriceCategory,
    edits?: Record<string, unknown>
  ): Promise<PriceCategory> {
    return await this.edit(
      PriceCategory.getFactory(),
      p,
      PriceCategory.getUrl(p.id),
      undefined,
      edits
    )
  }

  async editUser(u: User, edits?: Record<string, unknown>): Promise<User> {
    const updated = await this.edit(
      User.getFactory(),
      u,
      User.getUrl(u.id),
      this.users$,
      edits
    )
    const session = this.cache
    if (session?.userId === updated.id) {
      session.setUser(updated)
      this.notifyNavigationLabelsChanged()
    }
    return updated
  }

  async terminateSession(
    s: Session,
    options?: { doNotUnlock?: boolean; refundStrategy?: RefundStrategy }
  ): Promise<Session> {
    const c = this.cache

    if (!c) {
      throw new Error("Can't terminate session without being signed in")
    }

    if (!s) {
      throw new Error('Session must be present.')
    }

    const url = '/sites/' + s.siteId + '/sessions/' + s.id + '/terminate'
    const request = new SessionTerminateRequest()
    if (options?.doNotUnlock !== undefined) {
      request.doNotUnlock = options.doNotUnlock
    }
    if (options?.refundStrategy) {
      request.refundStrategy = options.refundStrategy
    }

    const res = (await this.send(
      'PUT',
      url,
      { data: request },
      c
    )) as DataResponse
    if (!res.data) {
      throw new Error('Data not found.')
    }

    const sessionFactory = Session.getFactory()
    s = sessionFactory.make(res.data)
    await this.putEntity(s, sessionFactory)

    await this.sessions$.next(s)
    await this.sessions$.next(new SubscriberSentinel())

    if (res.extra && res.extra.column) {
      const columnFactory = Column.getFactory()
      const c = columnFactory.make(res.extra.column)

      await this.putEntity(c, columnFactory)
      await this.columns$.next(c)
      await this.columns$.next(new SubscriberSentinel())
    }

    if (res.extra && res.extra.payments) {
      const paymentFactory = Payment.getFactory()
      const payments = res.extra.payments.map((p: any) =>
        paymentFactory.make(p)
      )
      for (const payment of payments) {
        await this.putEntity(payment, paymentFactory)
      }
    }

    return s
  }

  /**
   * sends a payment receipt email (POST …/payments/{id}/receipt_send).
   */
  async sendSessionPaymentReceipt(
    siteId: string,
    sessionId: string,
    paymentId: string,
    recipientEmail: string
  ): Promise<void> {
    if (!siteId || !sessionId || !paymentId || !recipientEmail) {
      throw new Error(
        'siteId, sessionId, paymentId and recipientEmail are required.'
      )
    }

    await this.isReady()
    if (!this.cache) {
      throw new Error('Cache was not initialized.')
    }

    const url =
      '/sites/' +
      siteId +
      '/sessions/' +
      sessionId +
      '/payments/' +
      paymentId +
      '/receipt_send'

    await this.send('POST', url, { data: recipientEmail }, this.cache)
  }

  async unlockLocker(l: Locker, siteId: string) {
    const c = this.cache

    if (!c) {
      throw new Error("Can't terminate locker without being signed in")
    }

    if (!l) {
      throw new Error('Locker must be present.')
    }

    await this.send(
      'PUT',
      '/sites/' + siteId + '/lockers/' + l.id + '/unlock',
      {},
      this.cache
    )
  }

  async addRolesToUser(
    userId: string,
    sub: string | undefined,
    scope: Scope
  ): Promise<User> {
    const c = this.cache

    if (!c) {
      throw new Error("Can't add roles without being signed in.")
    }

    if (!userId) {
      throw new Error('User ID must be provided.')
    }

    if (scope === undefined || scope === null) {
      throw new Error('Scopes must be provided.')
    }

    const flg = scope.toString()

    const subParam = sub ?? '_'

    const res = (await this.send(
      'PUT',
      `/users/${userId}/roles/${subParam}/${flg}`,
      {},
      c
    )) as DataResponse

    if (!res.data) {
      throw new Error('Failed to add roles to user.')
    }

    const userFactory = User.getFactory()
    const user = userFactory.make(res.data)
    await this.putEntity(user, userFactory)
    await this.users$.next(user)
    await this.users$.next(new SubscriberSentinel())

    return user
  }

  async deleteRoleFromUser(
    userId: string,
    sub: string | undefined,
    scope: Scope
  ): Promise<User> {
    const c = this.cache

    if (!c) {
      throw new Error("Can't delete roles without being signed in.")
    }

    if (!userId) {
      throw new Error('User ID must be provided.')
    }

    if (scope === undefined || scope === null) {
      throw new Error('Scopes must be provided.')
    }

    const flg = scope.toString()

    const subParam = sub ?? '_'

    const res = (await this.send(
      'DELETE',
      `/users/${userId}/roles/${subParam}/${flg}`,
      undefined,
      c
    )) as DataResponse

    if (!res.data) {
      throw new Error('Failed to delete roles from user.')
    }

    const userFactory = User.getFactory()
    const user = userFactory.make(res.data)

    await this.putEntity(user, userFactory)
    await this.users$.next(user)
    await this.users$.next(new SubscriberSentinel())
    return user
  }

  async editBookingSourceGroup(
    bookingSourceGroup: BookingSourceGroup,
    edits?: Record<string, unknown>
  ): Promise<BookingSourceGroup> {
    return await this.edit(
      BookingSourceGroup.getFactory(),
      bookingSourceGroup,
      BookingSourceGroup.getUrl(bookingSourceGroup.id),
      this.bookingSourceGroups$,
      edits
    )
  }

  async editLockerCondition(
    lockerId: string,
    siteId: string,
    condition: Condition
  ): Promise<Column> {
    const url = `/sites/${siteId}/lockers/${lockerId}/condition`

    const res = (await this.send(
      'PUT',
      url,
      { data: condition },
      this.cache
    )) as DataResponse
    if (!res.data) {
      throw new Error('Data not found.')
    }

    const columnFactory = Column.getFactory()
    const column = columnFactory.make(res.data)

    await this.putEntity(column, columnFactory)
    await this.columns$.next(column)
    await this.columns$.next(new SubscriberSentinel())

    this.syncActiveColumnAndNotifyNavigation(column)
    return column
  }

  async setColumnHiddenFromWatchdog(
    siteId: string,
    columnId: string,
    hideFromWatchdog: boolean
  ): Promise<Column> {
    return this.putColumnWatchdogVisibility(
      `/sites/${siteId}/columns/${columnId}/hide_from_watchdog`,
      hideFromWatchdog
    )
  }

  async setPaymentTerminalHiddenFromWatchdog(
    siteId: string,
    terminalLockerId: string,
    hideFromWatchdog: boolean
  ): Promise<Column> {
    return this.putColumnWatchdogVisibility(
      `/sites/${siteId}/lockers/${terminalLockerId}/payment_terminal/hide_from_watchdog`,
      hideFromWatchdog
    )
  }

  async setPaymentTerminalType(
    siteId: string,
    terminalLockerId: string,
    request: PaymentTerminalTypeChangeRequest
  ): Promise<Column> {
    const res = (await this.send(
      'PUT',
      `/sites/${siteId}/lockers/${terminalLockerId}/payment_terminal/type`,
      { data: request },
      this.cache
    )) as DataResponse
    if (!res.data) {
      throw new Error('Data not found.')
    }

    const columnFactory = Column.getFactory()
    const column = columnFactory.make(res.data)

    await this.putEntity(column, columnFactory)
    await this.columns$.next(column)
    await this.columns$.next(new SubscriberSentinel())

    this.syncActiveColumnAndNotifyNavigation(column)
    return column
  }

  async clearColumnFirmwareUpdate(
    siteId: string,
    columnId: string
  ): Promise<Column> {
    const column = await this.getColumn(siteId, columnId, false)

    await this.send(
      'DELETE',
      `/sites/${siteId}/columns/${columnId}/firmware/update`,
      undefined,
      this.cache
    )

    if (column.firmware) {
      column.firmware.update = undefined
    }

    await this.putEntity(column, Column.getFactory())
    await this.columns$.next(column)
    await this.columns$.next(new SubscriberSentinel())

    this.syncActiveColumnAndNotifyNavigation(column)
    return column
  }

  private async putColumnWatchdogVisibility(
    url: string,
    hideFromWatchdog: boolean
  ): Promise<Column> {
    const res = (await this.send(
      'PUT',
      url,
      { data: hideFromWatchdog },
      this.cache
    )) as DataResponse
    if (!res.data) {
      throw new Error('Data not found.')
    }

    const columnFactory = Column.getFactory()
    const column = columnFactory.make(res.data)

    await this.putEntity(column, columnFactory)
    await this.columns$.next(column)
    await this.columns$.next(new SubscriberSentinel())

    this.syncActiveColumnAndNotifyNavigation(column)
    return column
  }
  //#endregion

  //#region delete

  private async delete<T extends Identifiable>(
    factory: Factory<T>,
    o: T,
    url: string,
    observable: AwaitingSubject<T>
  ): Promise<T> {
    if (!o) {
      throw new Error('Object must be present.')
    }

    await this.isReady()

    const cache = this.cache
    if (!cache) {
      throw new Error('Cannot add an object without being signed in.')
    }

    const res = (await this.send(
      'DELETE',
      url,
      undefined,
      cache
    )) as DataResponse
    if (!res.data) {
      throw new Error('Data not found.')
    }

    o = factory.make(res.data)
    await this.putEntity(o, factory)

    if (observable) {
      await observable.next(o)
      await observable.next(new SubscriberSentinel())
    }

    return o
  }

  async deleteCustomer(c: Customer): Promise<Customer> {
    return this.delete(
      Customer.getFactory(),
      c,
      Customer.getUrl(c.id),
      this.customers$
    )
  }

  async deleteCountry(c: Country): Promise<Country> {
    return this.delete(
      Country.getFactory(),
      c,
      Country.getUrl(c.id),
      this.countries$
    )
  }

  async deletePresence(p: Presence): Promise<Presence> {
    return this.delete(
      Presence.getFactory(),
      p,
      Presence.getUrl(p.id),
      this.presences$
    )
  }

  async deleteSite(s: Site): Promise<Site> {
    return this.delete(Site.getFactory(), s, Site.getUrl(s.id), this.sites$)
  }

  async deleteLocation(location: Location): Promise<Location> {
    return this.delete(
      Location.getFactory(),
      location,
      Location.getUrl(location.siteId, location.id),
      this.locations$
    )
  }

  async deleteColumn(column: Column): Promise<Column> {
    return this.delete(
      Column.getFactory(),
      column,
      Column.getUrl(column.siteId, column.id),
      this.columns$
    )
  }

  async deleteLocker(b: Locker): Promise<Locker> {
    return this.delete(
      Locker.getFactory(),
      b,
      Locker.getUrl(b.siteId, b.id),
      this.lockers$
    )
  }

  async deleteUser(u: User): Promise<User> {
    return this.delete(User.getFactory(), u, User.getUrl(u.id), this.users$)
  }

  async deleteLockerDimensions(d: Dimensions): Promise<Dimensions> {
    const c = this.cache

    if (!d) {
      throw new Error('Dimensions must be present.')
    }

    if (!c) {
      throw new Error("Can't delete dimensions without being signed in")
    }

    const res = (await this.send(
      'DELETE',
      Dimensions.getUrl(d.id),
      undefined,
      c
    )) as DataResponse

    if (!res.data) {
      throw new Error('Data not found.')
    }

    return res.data
  }

  //#endregion

  //#region auth
  async signIn(user: User, password?: string): Promise<void> {
    if (!(user && user.email && password)) {
      throw new Error('User & Password must be present.')
    }

    const body = { data: user.email, extra: password }

    const res = (await this.send(
      'POST',
      '/users/signin/email',
      body
    )) as SigninResponse

    if (!res.data) {
      throw new Error('Data not found.')
    }

    const cache = new UserCache(
      res.data.accessToken,
      res.data.refreshToken,
      res.data.userId
    )

    localStorage.setItem('accessToken', cache.accessToken)
    localStorage.setItem('refreshToken', cache.refreshToken)
    localStorage.setItem('userId', cache.userId.toString())

    this.cache = cache

    this.userPollState.set(PollState.Awaiting)
    this.userPoll.postMessage({
      type: UserPollRequestType.Signin,
      apiUrl: this.apiUrl,
      acceptHeader: this.acceptHeader,
      accessToken: cache.accessToken,
      refreshToken: cache.refreshToken,
      userId: cache.userId,
      etag: await this.getUserCursor(res.data.userId),
    })
  }

  async resumeSession(): Promise<User | undefined> {
    const userId = localStorage.getItem('userId') ?? undefined
    const accessToken = localStorage.getItem('accessToken') ?? undefined
    const refreshToken = localStorage.getItem('refreshToken') ?? undefined

    if (!userId || !accessToken || !refreshToken) {
      return undefined
    }

    const cache = new UserCache(accessToken, refreshToken, userId)
    this.cache = cache
    this.userPollState.set(PollState.Awaiting)
    this.userPoll.postMessage({
      type: UserPollRequestType.Signin,
      apiUrl: this.apiUrl,
      acceptHeader: this.acceptHeader,
      accessToken: cache.accessToken,
      refreshToken: cache.refreshToken,
      userId: cache.userId,
      etag: await this.getUserCursor(userId),
    })

    await this.userHasOverviewData()

    try {
      const user = await this.getUser(userId)
      cache.setUser(user)

      return user
    } catch (error) {
      console.error('Failed to load user:', error)
      return undefined
    }
  }

  async claimPhone(phone: string, language: LanguageCode): Promise<boolean> {
    if (!phone) {
      throw new Error('Trying to log in without phone.')
    }

    const body = { data: phone, extra: { language: language } }

    const res = (await this.send(
      'POST',
      '/users/claim/phone',
      body
    )) as DataResponse

    return Boolean(res.data)
  }

  async signInByPhone(phone: string, verificationCode: string) {
    if (!phone) {
      throw new Error('Trying to log in without phone.')
    }

    if (!verificationCode) {
      throw new Error('Trying to log in without verificationCode.')
    }

    const res = (await this.send('POST', '/users/signin/phone', {
      data: phone,
      extra: { phoneVerificationCode: verificationCode },
    })) as SigninResponse

    if (!res.data) {
      throw new Error('Response contained no data when logging in by phone.')
    }

    const cache = new UserCache(
      res.data.accessToken,
      res.data.refreshToken,
      res.data.userId
    )

    localStorage.setItem('accessToken', cache.accessToken)
    localStorage.setItem('refreshToken', cache.refreshToken)
    localStorage.setItem('userId', cache.userId)

    this.cache = cache

    this.userPollState.set(PollState.Awaiting)
    this.userPoll.postMessage({
      type: UserPollRequestType.Signin,
      apiUrl: this.apiUrl,
      acceptHeader: this.acceptHeader,
      accessToken: cache.accessToken,
      refreshToken: cache.refreshToken,
      userId: cache.userId,
      etag: await this.getUserCursor(cache.userId),
    })
  }

  async claimEmail(
    email: string,
    language: LanguageCode,
    onlyClaimIfExisting: boolean = true
  ): Promise<boolean> {
    if (!email) {
      throw new Error('Trying to claim email without email.')
    }

    const body = {
      data: email,
      extra: { onlyClaimIfExisting: onlyClaimIfExisting, language: language },
    }

    const res = (await this.send(
      'POST',
      '/users/claim/email',
      body
    )) as DataResponse

    return Boolean(res.data)
  }

  async signInByEmail(email: string, verificationCode: string) {
    if (!email) {
      throw new Error('Trying to log in without email.')
    }

    if (!verificationCode) {
      throw new Error('Trying to log in without verificationCode.')
    }

    const res = (await this.send('POST', '/users/signin/email', {
      data: email,
      extra: { emailVerificationCode: verificationCode },
    })) as SigninResponse

    if (!res.data) {
      throw new Error('Response contained no data when logging in by email.')
    }

    const cache = new UserCache(
      res.data.accessToken,
      res.data.refreshToken,
      res.data.userId
    )

    localStorage.setItem('accessToken', cache.accessToken)
    localStorage.setItem('refreshToken', cache.refreshToken)
    localStorage.setItem('userId', cache.userId)

    this.cache = cache

    this.userPollState.set(PollState.Awaiting)
    this.userPoll.postMessage({
      type: UserPollRequestType.Signin,
      apiUrl: this.apiUrl,
      acceptHeader: this.acceptHeader,
      accessToken: cache.accessToken,
      refreshToken: cache.refreshToken,
      userId: cache.userId,
      etag: await this.getUserCursor(cache.userId),
    })
  }

  async signOut(persistUser: boolean = false) {
    if (!persistUser) {
      this.userPoll.postMessage({ type: UserPollRequestType.Signout })
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('userId')
      this.cache = undefined
      this.userPollState.set(null)
    }

    this.releasePollLock(PollKind.Location)
    this.releasePollLock(PollKind.Presence)
    this.releasePollLock(PollKind.Site)
    this.releasePollLock(PollKind.SiteLog)
    this.locationPoll.postMessage({ type: LocationPollRequestType.Signout })
    this.presencePoll.postMessage({ type: PresencePollRequestType.Signout })
    this.sitePoll.postMessage({ type: SitePollRequestType.Signout })
    this.siteLogPoll.postMessage({ type: SiteLogPollRequestType.Signout })
    this.presencePollState.set(null)
    this.sitePollState.set(null)
    this.siteLogPollState.set(null)
    this.locationPollState.set(null)
    localStorage.removeItem('presenceId')
    localStorage.removeItem('siteId')
    localStorage.removeItem('locationId')
  }

  async resetCache(broadcast: boolean = true) {
    // Other tabs clear their own polls and writes too, so none of them writes
    // a cursor for data that is gone.
    if (broadcast) {
      this.pollChannel.postMessage({ type: 'reset' })
    }
    this.userPoll.postMessage({ type: UserPollRequestType.Signout })
    this.releasePollLock(PollKind.Location)
    this.releasePollLock(PollKind.Presence)
    this.releasePollLock(PollKind.Site)
    this.releasePollLock(PollKind.SiteLog)
    this.locationPoll.postMessage({ type: LocationPollRequestType.Signout })
    this.presencePoll.postMessage({ type: PresencePollRequestType.Signout })
    this.sitePoll.postMessage({ type: SitePollRequestType.Signout })
    this.siteLogPoll.postMessage({ type: SiteLogPollRequestType.Signout })
    this.userPollState.set(null)
    this.presencePollState.set(null)
    this.sitePollState.set(null)
    this.siteLogPollState.set(null)
    this.locationPollState.set(null)
    for (const state of this.pollCursorStates.values()) {
      state.set(null)
    }
    this.pollHeavyTargets.clear()
    this.autoBackfillDone.clear()
    this.cache = undefined
    this.presenceCache = undefined
    this.siteCache = undefined
    this.siteLogCache = undefined
    this.locationCache = undefined
    await Promise.all([
      this.userPollWriteQueue.drain(),
      this.presencePollWriteQueue.drain(),
      this.sitePollWriteQueue.drain(),
      this.siteLogPollWriteQueue.drain(),
      this.locationPollWriteQueue.drain(),
    ])

    // A reset another tab started has cleared the shared storage already;
    // clearing it again here could wipe what that tab has polled since and
    // leave its user cursor without the data it describes.
    if (!broadcast) {
      return
    }

    localStorage.clear()

    await new Promise((resolve, reject) => {
      const openRequest = indexedDB.open('SpeedBoxDB')

      openRequest.onerror = event => {
        reject(event)
      }

      openRequest.onsuccess = _event => {
        const db = openRequest.result
        const objectStoreNames = Array.from(db.objectStoreNames)

        if (objectStoreNames.length === 0) {
          db.close()
          resolve(null)
          return
        }

        const transaction = db.transaction(objectStoreNames, 'readwrite')

        transaction.onerror = event => {
          db.close()
          reject(event)
        }

        transaction.oncomplete = () => {
          db.close()
          resolve(null)
        }

        objectStoreNames.forEach(storeName => {
          const objectStore = transaction.objectStore(storeName)
          const clearRequest = objectStore.clear()

          clearRequest.onerror = _event => {
            console.warn(`Error clearing object store ${storeName}.`, _event)
          }

          clearRequest.onsuccess = () => {
            console.log(`Object store ${storeName} cleared.`)
          }
        })
      }

      openRequest.onupgradeneeded = _event => {
        const db = openRequest.result
        this.setupDbStores(db, _event)

        const objectStoreNames = Array.from(db.objectStoreNames)

        if (objectStoreNames.length === 0) {
          db.close()
          resolve(null)
          return
        }

        const transaction = db.transaction(objectStoreNames, 'readwrite')

        transaction.onerror = event => {
          db.close()
          reject(event)
        }

        transaction.oncomplete = () => {
          db.close()
          resolve(null)
        }

        objectStoreNames.forEach(storeName => {
          const objectStore = transaction.objectStore(storeName)
          const clearRequest = objectStore.clear()

          clearRequest.onerror = event => {
            console.warn(`Error clearing object store ${storeName}.`, event)
          }

          clearRequest.onsuccess = () => {
            console.log(`Object store ${storeName} cleared.`)
          }
        })
      }
    })

    this.cache = undefined
  }

  async clearTable(table: string): Promise<void> {
    const db = this.db
    await this.isReady()
    if (!db) {
      throw new Error('DB not initialized.')
    }
    const transaction = db.transaction(table, 'readwrite')
    const objectStore = transaction.objectStore(table)
    const clearRequest = objectStore.clear()

    return new Promise<void>((resolve, reject) => {
      clearRequest.onsuccess = () => {
        console.log(`Table ${table} cleared.`)
        resolve()
      }
      clearRequest.onerror = event => {
        console.error(`Error clearing table ${table}:`, event)
        reject(event)
      }
    })
  }

  //#endregion

  //#region communications

  private async send(
    method: string,
    endpoint: string,
    body?: object,
    cache?: UserCache,
    cursor?: string,
    refresh: boolean = true,
    range?: string
  ): Promise<object> {
    if (method === 'POST' || method === 'PUT') {
      if (!body) {
        throw new Error('Body can not be undefined for POST or PUT requests.')
      }
    } else if (body) {
      throw new Error('Only POST and PUT requests can have a body.')
    }

    const options: any = {
      headers: {
        Accept: this.acceptHeader,
      },
    }

    if (cache) {
      options['headers']['Authorization'] = 'Bearer ' + cache.accessToken
    }

    if (range) {
      options['headers']['Range'] = range
    }

    if (method === 'PUT') {
      options['Content-Type'] = 'multipart/form-data;'
    }

    try {
      let response: object
      if (method === 'GET') {
        response = await firstValueFrom(
          this.http.get(this.apiUrl + endpoint, options)
        )
      } else if (method === 'POST') {
        response = await firstValueFrom(
          this.http.post(this.apiUrl + endpoint, body, options)
        )
      } else if (method === 'PUT') {
        response = await firstValueFrom(
          this.http.put(this.apiUrl + endpoint, body, options)
        )
      } else if (method === 'DELETE') {
        response = await firstValueFrom(
          this.http.delete(this.apiUrl + endpoint, options)
        )
      } else {
        throw new Error('Unrecognized method type.')
      }

      this.networkDiagnostics.notifyApiSuccess()
      return response
    } catch (error: any) {
      if (error?.status === 0) {
        this.networkDiagnostics.report(method, endpoint, true)
      }

      if (error && error.status === 401 && cache && refresh === true) {
        try {
          if (!cache) {
            throw new Error('Cache was uninitialized when refreshing token.')
          }
          await this.refreshToken()
          return this.send(method, endpoint, body, cache, cursor, false, range)
        } catch (refreshError: any) {
          const fault = refreshError.error?.fault
          if (fault) {
            throw new Fault(fault)
          } else {
            throw refreshError
          }
        }
      } else {
        const fault = error?.error?.fault
        if (fault) {
          throw new Fault(fault)
        } else {
          throw error
        }
      }
    }
  }

  async refreshToken() {
    if (!this.cache) {
      throw new Error('Cache service must be initialized before token refresh.')
    }

    const body = { data: this.cache.refreshToken }

    const res = (await this.send(
      'POST',
      '/users/' + this.cache.userId + '/token/refresh',
      body,
      undefined,
      undefined,
      false
    )) as DataResponse

    if (!res.data) {
      throw new Error('Token refresh response has no data.')
    }

    this.cache.accessToken = res.data
    localStorage.setItem('accessToken', this.cache.accessToken)
  }

  //#endregion

  //#region subscription
  private async subscribe<T extends Deletable>(
    factory: Factory<T>,
    observable: AwaitingSubject<T>,
    next: (value: T | SubscriberSentinel) => Promise<void>,
    deleted: boolean = true,
    provideAll: boolean = true
  ): Promise<Subscription<T>> {
    const c = this.cache
    if (!c) {
      throw new Error('Cannot subscribe without being signed in.')
    }

    await this.isReady()

    const s = observable.subscribe({
      next: async c => {
        if (c instanceof SubscriberSentinel || deleted || !c.deleted) {
          await next(c)
        }
      },
    })

    if (provideAll) {
      const entities = await this.getAllEntitiesFromDb(factory)

      for (let i = 0; i < entities.length; i++) {
        if (deleted || !entities[i].deleted) {
          await next(entities[i])
        }
      }
    }

    await next(new SubscriberSentinel())

    return s
  }

  public async subscribeToLogEntries(
    next: (value: Entry | SubscriberSentinel) => Promise<void>,
    provideAll: boolean = true
  ): Promise<Subscription<Entry>> {
    return this.subscribe(
      Entry.getFactory(),
      this.logEntries$,
      next,
      true,
      provideAll
    )
  }

  public async subscribeToCountries(
    next: (value: Country | SubscriberSentinel) => Promise<void>,
    provideAll: boolean = true
  ): Promise<Subscription<Country>> {
    return this.subscribe(
      Country.getFactory(),
      this.countries$,
      next,
      true,
      provideAll
    )
  }

  public async subscribeToUsers(
    next: (value: User | SubscriberSentinel) => Promise<void>,
    provideAll: boolean = true
  ): Promise<Subscription<User>> {
    return this.subscribe(
      User.getFactory(),
      this.users$,
      next,
      true,
      provideAll
    )
  }

  public async subscribeToCustomers(
    next: (value: Customer | SubscriberSentinel) => Promise<void>,
    provideAll: boolean = true
  ): Promise<Subscription<Customer>> {
    return this.subscribe(
      Customer.getFactory(),
      this.customers$,
      next,
      true,
      provideAll
    )
  }

  public async subscribeToPresences(
    next: (value: Presence | SubscriberSentinel) => Promise<void>,
    provideAll: boolean = true
  ): Promise<Subscription<Presence>> {
    return this.subscribe(
      Presence.getFactory(),
      this.presences$,
      next,
      true,
      provideAll
    )
  }

  public async subscribeToSites(
    next: (value: Site | SubscriberSentinel) => Promise<void>,
    provideAll: boolean = true,
    presenceId?: string
  ): Promise<Subscription<Site>> {
    const c = this.cache
    if (!c) {
      throw new Error('Cannot subscribe without being signed in.')
    }

    await this.isReady()

    const s = this.sites$.subscribe({
      next: async c => {
        if (c instanceof SubscriberSentinel || !c.deleted) {
          await next(c)
        }
      },
    })

    if (provideAll) {
      let entities: Site[]

      if (presenceId) {
        entities = await this.getSitesByPresenceId(presenceId)
      } else {
        entities = await this.getAllEntitiesFromDb(Site.getFactory())
      }

      const nonDeletedSites = entities.filter(site => !site.deleted)

      for (let i = 0; i < nonDeletedSites.length; i++) {
        await next(nonDeletedSites[i])
        await new Promise(resolve => setTimeout(resolve, 50))
      }
    }

    await next(new SubscriberSentinel())

    return s
  }

  public async subscribeToLocations(
    next: (value: Location | SubscriberSentinel) => Promise<void>,
    provideAll: boolean = true
  ): Promise<Subscription<Location>> {
    return this.subscribe(
      Location.getFactory(),
      this.locations$,
      next,
      true,
      provideAll
    )
  }

  public async subscribeToColumns(
    next: (value: Column | SubscriberSentinel) => Promise<void>,
    provideAll: boolean = true
  ): Promise<Subscription<Column>> {
    return this.subscribe(
      Column.getFactory(),
      this.columns$,
      next,
      true,
      provideAll
    )
  }

  public async subscribeToSessions(
    next: (value: Session | SubscriberSentinel) => Promise<void>,
    provideAll: boolean = true
  ): Promise<Subscription<Session>> {
    return this.subscribe(
      Session.getFactory(),
      this.sessions$,
      next,
      true,
      provideAll
    )
  }

  public async subscribeToLockerPriceCategory(
    next: (value: PriceCategory | SubscriberSentinel) => Promise<void>,
    provideAll: boolean = true
  ): Promise<Subscription<PriceCategory>> {
    return this.subscribe(
      PriceCategory.getFactory(),
      this.lockerPriceCategories$,
      next,
      true,
      provideAll
    )
  }

  public async subscribeToLockerDimensions(
    next: (value: Dimensions | SubscriberSentinel) => Promise<void>,
    provideAll: boolean = true
  ): Promise<Subscription<Dimensions>> {
    const c = this.cache
    if (!c) {
      throw new Error('Cannot subscribe without being signed in.')
    }

    await this.isReady()

    const s = this.lockerDimensions$.subscribe({
      next: async c => {
        if (c instanceof SubscriberSentinel) {
          await next(c)
        }
      },
    })

    if (provideAll) {
      const entities = await this.getAllEntitiesFromDb(Dimensions.getFactory())
      for (let i = 0; i < entities.length; i++) {
        await next(entities[i])
      }
      await next(new SubscriberSentinel())
    }
    return s
  }

  public async subscribeToLockers(
    next: (value: Locker | SubscriberSentinel) => Promise<void>,
    provideAll: boolean = true
  ): Promise<Subscription<Locker>> {
    return this.subscribe(
      Locker.getFactory(),
      this.lockers$,
      next,
      true,
      provideAll
    )
  }

  public async subscribeToBookingSourceGroups(
    next: (value: BookingSourceGroup | SubscriberSentinel) => Promise<void>,
    provideAll: boolean = true
  ): Promise<Subscription<BookingSourceGroup>> {
    return this.subscribe(
      BookingSourceGroup.getFactory(),
      this.bookingSourceGroups$,
      next,
      true,
      provideAll
    )
  }

  //#region utils
  public async isReady(): Promise<void> {
    return new Promise<void>(resolve => {
      const check = () => {
        if (this.db) {
          if (!this.db.objectStoreNames.contains('reportsDaily')) {
            console.log(
              '⚠️ reportsDaily store missing, forcing database upgrade...'
            )
            this.forceDatabaseUpgrade()
              .then(() => resolve())
              .catch(() => resolve())
            return
          }
          resolve()
          return
        } else {
          setTimeout(check, 0)
        }
      }
      check()
    })
  }

  async sleep(milliseconds: number) {
    return new Promise(resolve => setTimeout(resolve, milliseconds))
  }

  async forceDatabaseUpgrade(): Promise<void> {
    console.log('🔄 Forcing database upgrade...')

    if (this.db) {
      this.db.close()
      this.db = undefined
    }

    await new Promise<void>((resolve, reject) => {
      const deleteRequest = indexedDB.deleteDatabase('SpeedBoxDB')
      deleteRequest.onsuccess = () => {
        console.log('🗑️ Database deleted, recreating...')
        resolve()
      }
      deleteRequest.onerror = () => {
        console.error('❌ Failed to delete database:', deleteRequest.error)
        reject(deleteRequest.error)
      }
    })

    await this.sleep(100)

    await new Promise<void>((resolve, reject) => {
      const request = indexedDB.open('SpeedBoxDB', environment.dbVersion)

      request.onerror = () => {
        console.error('❌ Failed to reopen database:', request.error)
        reject(request.error)
      }

      request.onsuccess = () => {
        this.db = request.result
        console.log('✅ Database recreated successfully')
        resolve()
      }

      request.onupgradeneeded = event => {
        console.log('🔄 Running database upgrade...')
        this.setupDbStores(request.result, event)
      }
    })
  }

  //#endregion

  public async getSessions(
    activeEntity: Entity,
    fromDate?: Date | string | null,
    untilDate?: Date | string | null
  ): Promise<SessionConfirmed[]> {
    let indexName: string
    let entityId: string

    if (activeEntity instanceof Customer) {
      indexName = 'customerId'
      entityId = activeEntity.id
    } else if (activeEntity instanceof Presence) {
      indexName = 'presenceId'
      entityId = activeEntity.id
    } else if (activeEntity instanceof Site) {
      indexName = 'siteId'
      entityId = activeEntity.id
    } else if (activeEntity instanceof Location) {
      indexName = 'locationId'
      entityId = activeEntity.id
    } else if (activeEntity instanceof Column) {
      indexName = 'columnId'
      entityId = activeEntity.id
    } else if (activeEntity instanceof Locker) {
      indexName = 'lockerId'
      entityId = activeEntity.id
    } else {
      return []
    }

    const sessions = await this.getEntitiesFromDbByIndex(
      { lower: entityId, upper: entityId },
      indexName,
      Session.getFactory()
    )

    const from = fromDate ? new Date(fromDate) : new Date(0)
    const until = untilDate ? new Date(untilDate) : new Date('9999-12-31')

    const filteredSessions = sessions.filter(session => {
      if (!(session instanceof SessionConfirmed)) {
        return false
      }

      const sessionDate = session.created
      return sessionDate >= from && sessionDate <= until
    }) as SessionConfirmed[]

    return filteredSessions
  }

  private async getDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('SpeedBoxDB', environment.dbVersion)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result)
      request.onupgradeneeded = event => {
        const db = (event.target as IDBOpenDBRequest).result
        this.setupDbStores(db, event)
      }
    })
  }

  async fixDatabaseSchema(): Promise<void> {
    console.log('Fixing database schema...')

    if (this.db) {
      this.db.close()
      this.db = undefined
    }

    await new Promise<void>((resolve, reject) => {
      const deleteRequest = indexedDB.deleteDatabase('SpeedBoxDB')

      deleteRequest.onerror = () => {
        console.error('Failed to delete database:', deleteRequest.error)
        reject(deleteRequest.error)
      }

      deleteRequest.onsuccess = () => {
        console.log('Database deleted successfully')
        resolve()
      }
    })

    await new Promise(resolve => setTimeout(resolve, 500))

    await new Promise<void>((resolve, reject) => {
      const request = indexedDB.open('SpeedBoxDB', environment.dbVersion + 1)

      request.onerror = () => {
        console.error('Failed to recreate database:', request.error)
        reject(request.error)
      }

      request.onsuccess = () => {
        console.log(
          'Database recreated successfully with version:',
          request.result.version
        )
        request.result.close()
        resolve()
      }

      request.onupgradeneeded = event => {
        console.log('Database upgrade triggered, creating indexes...')
        const db = request.result
        this.setupDbStores(db, event)
      }
    })

    await new Promise(resolve => setTimeout(resolve, 200))

    await this.isReady()

    console.log('Database schema fix completed')
  }

  async emergencyDatabaseReset(): Promise<void> {
    try {
      if (this.db) {
        this.db.close()
        this.db = undefined
      }

      await new Promise<void>((resolve, reject) => {
        const deleteRequest = indexedDB.deleteDatabase('SpeedBoxDB')

        deleteRequest.onerror = () => {
          console.error('Failed to delete database:', deleteRequest.error)
          reject(deleteRequest.error)
        }

        deleteRequest.onsuccess = () => {
          console.log('Database deleted successfully')
          resolve()
        }
      })

      await new Promise(resolve => setTimeout(resolve, 1000))

      localStorage.clear()
      sessionStorage.clear()

      sessionStorage.setItem('wasHardReset', 'true')
    } catch (error) {
      console.error('Emergency database reset failed:', error)
      throw error
    }
  }

  async forceSitePoll(): Promise<void> {
    if (!this.cache || !this.siteCache) {
      throw new Error('Cannot force Site poll without being signed in.')
    }
    await this.isReady()
    if (
      !this.polledByThisTab.has(PollKind.Site + ':' + this.siteCache.siteId)
    ) {
      return
    }
    this.siteCache.polled = false
    this.sitePollState.set(PollState.Loading)
    this.sitePoll.postMessage({
      type: SitePollRequestType.ForcePoll,
      apiUrl: this.apiUrl,
      acceptHeader: this.acceptHeader,
      accessToken: this.cache.accessToken,
      refreshToken: this.cache.refreshToken,
      userId: this.cache.userId,
      siteId: this.siteCache.siteId,
    })
  }

  /**
   * fetches every log entry of the given types for a site (or one of its
   * columns), back to the first, for the settings history. One-off: no poll
   * cursor is read or moved and nothing is stored in the site log cache.
   */
  async fetchSettingsHistory(
    siteId: string,
    types: string[],
    columnId?: string
  ): Promise<unknown[]> {
    await this.isReady()

    const params = ['types=' + encodeURIComponent(types.join(','))]
    if (columnId) {
      params.push('columnId=' + encodeURIComponent(columnId))
    }

    const res = (await this.send(
      'GET',
      '/sites/' + siteId + '/log_poll?' + params.join('&'),
      undefined,
      this.cache,
      undefined,
      true,
      'items=0-'
    )) as any

    return res?.data?.logEntries ?? []
  }

  /**
   * fetches the log entries of one session (all of them), or of one column
   * (the 1,000 created just before what the site log poll has cached), with
   * the site log poll's filters, and stores them. One-off: no poll cursor
   * moves.
   */
  async fetchSiteLogEntries(
    siteId: string,
    filter: { sessionId?: string; columnId?: string }
  ): Promise<void> {
    await this.isReady()

    const params: string[] = []
    if (filter.sessionId) {
      params.push('sessionId=' + encodeURIComponent(filter.sessionId))
    }
    if (filter.columnId) {
      params.push('columnId=' + encodeURIComponent(filter.columnId))
      const cursor = await this.getPollCursor(PollKind.SiteLog, siteId)
      if (cursor && cursor.heavyOldest > 0) {
        params.push(
          'createdTo=' +
            encodeURIComponent(
              new Date(cursor.heavyOldest * 1000).toISOString()
            )
        )
      }
    }

    const res = (await this.send(
      'GET',
      '/sites/' + siteId + '/log_poll?' + params.join('&'),
      undefined,
      this.cache,
      undefined,
      true,
      filter.sessionId ? 'items=0-' : 'items=0-999'
    )) as any

    const entries = res?.data?.logEntries ?? []
    if (entries.length > 0) {
      await this.batchParsePollData(
        this.siteLogCache ?? new SiteLogCache(siteId),
        entries,
        Entry,
        this.logEntries$
      )
    }
  }

  /**
   * reads a site's light lists (the site, its locations and columns) once, without
   * polling the site: for pages that need them for sites that are not open, such as
   * the presence dashboard, whose sessions come with the presence poll.
   */
  async fetchSiteLightLists(siteId: string): Promise<void> {
    if (!this.cache) {
      throw new Error('Cannot read a site without being signed in.')
    }
    await this.isReady()

    const res = (await this.send(
      'GET',
      '/sites/' + siteId + '/poll?sessions=false',
      undefined,
      this.cache,
      undefined,
      true,
      'items=0-'
    )) as any

    const data = res?.data
    const sc = new SiteCache(siteId)
    if (data?.site) {
      await this.batchParsePollData(sc, [data.site], Site, this.sites$)
    }
    if (data?.locations?.length) {
      await this.batchParsePollData(
        sc,
        data.locations,
        Location,
        this.locations$
      )
    }
    if (data?.columns?.length) {
      await this.batchParsePollData(sc, data.columns, Column, this.columns$)
    }
  }

  /**
   * whether the cache already holds every session of a polled site, location or
   * presence from date until until, so they can be read from IndexedDB with no
   * poll.
   */
  async pollHeavyHolds(
    kind: PollKind,
    id: string,
    date: Date,
    until: Date
  ): Promise<boolean> {
    const cursor = await this.getPollCursor(kind, id)
    return (
      cursor !== undefined &&
      pollSpansHold(
        cursor,
        Math.floor(date.getTime() / 1000),
        Math.floor(until.getTime() / 1000)
      )
    )
  }

  /**
   * waits until the cached heavy lists of a site, location or presence reach back to
   * date (callers floor it to the entity's creation) and, with until, are caught up
   * to until; onProgress gets the cached
   * oldest edge while it waits. Stops when isCancelled says so (the page was left),
   * and fails when neither the cached edge nor any page of it has come for
   * POLL_COVER_STALL_MS.
   */
  async pollHeavyCovers(
    kind: PollKind,
    id: string,
    date: Date,
    onProgress?: (heavyOldest: Date | undefined) => void,
    isCancelled?: () => boolean,
    until?: Date
  ): Promise<void> {
    const state = this.pollCursorState(kind, id)
    const to = Math.floor(date.getTime() / 1000)
    const newest =
      until === undefined ? undefined : Math.floor(until.getTime() / 1000)
    const cursorId = kind + ':' + id
    let edge: string | undefined
    let pageAt = this.pollPageAt.get(cursorId)
    let movedAt = Date.now()
    for (;;) {
      if (isCancelled?.()) {
        throw new Error('Loading cancelled')
      }
      const c = state()
      onProgress?.(c ? new Date(c.heavyOldest * 1000) : undefined)
      // the live span reaches back to date and, with until, has caught up to it
      if (
        c &&
        !c.lightOnly &&
        c.heavyOldest <= to &&
        (newest === undefined || c.heavyNewest >= newest)
      ) {
        return
      }
      // A window of history arrives page by page and moves the edge only
      // when it is complete, so a page arriving counts as progress too.
      const now =
        c && !c.lightOnly ? c.heavyOldest + ':' + c.heavyNewest : undefined
      const page = this.pollPageAt.get(cursorId)
      if (now !== edge || page !== pageAt) {
        edge = now
        pageAt = page
        movedAt = Date.now()
      } else if (Date.now() - movedAt > POLL_COVER_STALL_MS) {
        throw new Error(
          'Loading sessions stopped making progress. Try again later.'
        )
      }
      await new Promise(resolve => setTimeout(resolve, 500))
    }
  }

  /**
   * polls a site's log, only on the pages that show it (SiteLogPollRouteService);
   * nothing happens when that site's log is polled already.
   */
  async startSiteLogPoll(siteId: string): Promise<void> {
    if (!this.cache) {
      throw new Error('Cannot start site log poll without being signed in.')
    }
    await this.isReady()
    const uc = this.cache
    if (this.siteLogCache?.siteId === siteId) {
      return
    }

    const slc = new SiteLogCache(siteId)
    this.siteLogCache = slc
    this.siteLogPollState.set(PollState.Awaiting)
    this.requestPollLock(
      PollKind.SiteLog,
      siteId,
      async () => {
        const cursor = await this.getPollCursor(PollKind.SiteLog, siteId)
        if (this.siteLogCache !== slc) {
          return
        }
        this.siteLogPoll.postMessage({
          type: SiteLogPollRequestType.Signin,
          apiUrl: this.apiUrl,
          acceptHeader: this.acceptHeader,
          accessToken: uc.accessToken,
          refreshToken: uc.refreshToken,
          userId: uc.userId,
          siteId: siteId,
          cursor: cursor,
          backfillTo: this.pollHeavyTargets.get(
            PollKind.SiteLog + ':' + siteId
          ),
        })
      },
      () =>
        this.siteLogPoll.postMessage({ type: SiteLogPollRequestType.Signout })
    )
  }

  stopSiteLogPoll(): void {
    if (!this.siteLogCache) {
      return
    }
    this.releasePollLock(PollKind.SiteLog)
    this.siteLogCache = undefined
    this.siteLogPollState.set(null)
  }

  /**
   * one tab polls each site, location or presence: the tab holding
   * the Web Lock poll:<kind>:<id>. Other tabs queue, read what it writes and
   * take over when it closes.
   */
  private requestPollLock(
    kind: PollKind,
    id: string,
    start: () => Promise<void>,
    stop: () => void
  ): void {
    this.releasePollLock(kind)

    const cursorId = kind + ':' + id
    const controller = new AbortController()
    let released = false
    let release: (() => void) | undefined

    this.pollLockReleases.set(kind, () => {
      released = true
      controller.abort()
      if (this.polledByThisTab.delete(cursorId)) {
        stop()
      }
      release?.()
    })

    const held = async () => {
      if (released) {
        return
      }
      this.polledByThisTab.add(cursorId)
      await start()
      if (released) {
        return
      }
      await new Promise<void>(resolve => (release = resolve))
    }

    // Web Locks exist only in a secure context (https, localhost): without
    // them each tab polls on its own.
    if (!navigator.locks) {
      void held()
      return
    }

    navigator.locks
      .request('poll:' + cursorId, { ifAvailable: true }, lock => {
        if (lock) {
          return held()
        }
        void this.readPolledByOtherTab(kind, id)
        return navigator.locks.request(
          'poll:' + cursorId,
          { signal: controller.signal },
          held
        )
      })
      .catch(err => {
        if (!released) {
          console.error('Failed to request poll lock:', err)
        }
      })
  }

  private releasePollLock(kind: PollKind): void {
    this.pollLockReleases.get(kind)?.()
    this.pollLockReleases.delete(kind)
  }

  private getActivePollCache(
    kind: PollKind,
    id: string
  ): [PollProgress, WritableSignal<PollState | null>] | undefined {
    switch (kind) {
      case PollKind.Site:
        return this.siteCache?.siteId === id
          ? [this.siteCache, this.sitePollState]
          : undefined
      case PollKind.SiteLog:
        return this.siteLogCache?.siteId === id
          ? [this.siteLogCache, this.siteLogPollState]
          : undefined
      case PollKind.Location:
        return this.locationCache?.locationId === id
          ? [this.locationCache, this.locationPollState]
          : undefined
      case PollKind.Presence:
        return this.presenceCache?.presenceId === id
          ? [this.presenceCache, this.presencePollState]
          : undefined
    }
  }

  // Another tab polls this site, location or presence: what it has written
  // is in IndexedDB.
  private async readPolledByOtherTab(kind: PollKind, id: string) {
    const cursor = await this.getPollCursor(kind, id)
    const active = this.getActivePollCache(kind, id)
    if (!cursor || !active) {
      return
    }

    const [cache, state] = active
    this.setPollCursorState(kind + ':' + id, cursor, false)
    if (!cache.polled) {
      cache.polled = true
      cache.markOverviewReady()
    }
    state.set(PollState.Ready)
  }

  private async handlePollChannelMessage(m: any): Promise<void> {
    if (m.type === 'reset') {
      await this.resetCache(false)
      location.assign('/')
      return
    }

    const cursorId = m.kind + ':' + m.id

    if (m.type === 'backfill') {
      if (this.polledByThisTab.has(cursorId)) {
        await this.pollHeavyBackTo(m.kind, m.id, new Date(m.to * 1000))
      }
      return
    }

    if (m.type !== 'commit') {
      return
    }

    this.pollPageAt.set(cursorId, Date.now())
    this.setPollCursorState(cursorId, m.cursor, m.backfilling)

    const active = this.getActivePollCache(m.kind, m.id)
    if (!active || this.polledByThisTab.has(cursorId)) {
      return
    }

    const [cache, state] = active
    if (!cache.polled) {
      cache.polled = true
      cache.markOverviewReady()
    }
    state.set(PollState.Ready)

    const db = this.db
    if (!db) {
      throw new Error('DB not initialized.')
    }

    const subjects: Record<string, [Factory<any>, AwaitingSubject<any>]> = {
      presences: [Presence.getFactory(), this.presences$],
      sites: [Site.getFactory(), this.sites$],
      locations: [Location.getFactory(), this.locations$],
      columns: [Column.getFactory(), this.columns$],
      sessions: [Session.getFactory(), this.sessions$],
      log_entries: [Entry.getFactory(), this.logEntries$],
      users: [User.getFactory(), this.users$],
    }

    for (const table of Object.keys(m.written)) {
      const subject = subjects[table]
      if (!subject) {
        continue
      }
      const [factory, observable] = subject

      const stored = await new Promise<any[]>((resolve, reject) => {
        const transaction = db.transaction([table], 'readonly')
        const store = transaction.objectStore(table)
        const results: any[] = []
        for (const id of m.written[table] as string[]) {
          const req = store.get(id)
          req.onsuccess = () => {
            if (req.result) {
              results.push(req.result)
            }
          }
        }
        transaction.oncomplete = () => resolve(results)
        transaction.onerror = event => reject(event)
      })

      for (const json of stored) {
        const o = factory.make(jsonFromStorage(json))
        if (o instanceof Site && this.siteCache?.siteId === o.id) {
          this.siteCache.setSite(o)
        } else if (
          o instanceof Location &&
          this.locationCache?.locationId === o.id
        ) {
          this.locationCache.setLocation(o)
        } else if (
          o instanceof Presence &&
          this.presenceCache?.presenceId === o.id
        ) {
          this.presenceCache.setPresence(o)
        }
        if (observable.subCount > 0) {
          await observable.next(o)
        }
      }
      if (stored.length > 0) {
        await observable.next(new SubscriberSentinel())
      }
    }
  }
}

export interface DataResponse {
  data: any
  extra: any
}

interface SigninResponse {
  data: {
    accessToken: string
    refreshToken: string
    userId: string
  }
}

class UserCache extends PollProgress {
  accessToken: string
  refreshToken: string
  userId: string
  user?: User

  constructor(accessToken: string, refreshToken: string, userId: string) {
    super()
    this.accessToken = accessToken
    this.refreshToken = refreshToken
    this.userId = userId
  }

  setUser(u: User) {
    this.user = u
  }
}

class PresenceCache extends PollProgress {
  userId: string
  presenceId: string
  presence?: Presence

  constructor(userId: string, presenceId: string) {
    super()
    this.userId = userId
    this.presenceId = presenceId
  }

  setPresence(presence: Presence) {
    this.presence = presence
  }
}

class SiteCache extends PollProgress {
  siteId: string
  site?: Site

  constructor(siteId: string) {
    super()
    this.siteId = siteId
  }

  setSite(site: Site) {
    this.site = site
  }
}

class SiteLogCache extends PollProgress {
  siteId: string

  constructor(siteId: string) {
    super()
    this.siteId = siteId
  }
}

class LocationCache extends PollProgress {
  siteId: string
  locationId: string
  location?: Location

  constructor(siteId: string, locationId: string) {
    super()
    this.siteId = siteId
    this.locationId = locationId
  }

  setLocation(location: Location) {
    this.location = location
  }
}

class ColumnCache {
  column?: Column

  setColumn(column: Column) {
    this.column = column
  }
}

class LockerCache {
  locker?: Locker

  setLocker(locker: Locker) {
    this.locker = locker
  }
}

class CountryCache {
  country?: Country

  setCountry(country: Country) {
    this.country = country
  }
}

class CustomerCache extends PollProgress {
  userId: string
  customerId: string
  customer?: Customer

  constructor(userId: string, customerId: string) {
    super()
    this.userId = userId
    this.customerId = customerId
  }

  setCustomer(customer: Customer) {
    this.customer = customer
  }
}
